import {OnTrip} from './components/OnTrip';
import {HomeVehiclePager} from "./components/HomeVehiclePager";
import {useActiveVehicle} from "./lib/activeVehicle";
import {unreadMessages,chatReadCopy,chatUiCopy} from "./lib/chatRead";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { type Route } from "./components/Nav";
import { IntegratedTools, type ToolId } from "./components/IntegratedTools";
import { ToolsCatalog } from "./components/ToolsCatalog";
import { MenuIcon } from "./components/MenuIcon";
import { AppFeedback, feedbackTitle } from "./components/AppFeedback";
import "./components/menu-density.css";
import "./components/premium-motion.css";
import {useMotionPresence} from './lib/useMotionPresence';
import { PrecisionIcon, type PrecisionIconName } from "./components/PrecisionIcon";
import { AccountEntry } from "./components/AccountEntry";
import { GuestLanding } from "./components/GuestLanding";
import { vehicleImage } from "./lib/vehicleImage";
import { VehicleInventory } from "./components/VehicleInventory";

const APP_RELEASE = "2026.09.14-checkout-cancellation-v111";
const APP_VERSION = "1.9.6";
const APP_UPDATED = "22.09.2026";
import {
  clearLocalVehicles,
  loadSettings,
  loadVehicles,
  saveSettings,
  saveVehicles,
} from "./lib/storage";
import { acceptVehicleDriverInvite, apiUrl, deleteAccount, exchangeDriveHandoff, getAuthToken, linkDossierVehicle, linkTvpfVehicle, loadAccountVehicles, loadVehicleAssistance, logoutAccount, resetPrecisionProfile, restoreAccountSession, saveAccountProfile, saveAccountPreferences, saveAccountVehicles, type AccountProfile, type AssistanceOverview } from "./lib/accountApi";
import {formatProfileAddress,parseProfileAddress} from './lib/profileAddress';
import { hydratePreferences, queuePreferences, readPreferences, acknowledgePreferences, type Preferences } from "./lib/preferences";
import type { Settings, Vehicle, VehicleEvent } from "./types";
import { assessBattery, vehicleAge } from "./lib/battery";
import { t } from "./lib/i18n";
import { useLiveTranslation } from "./lib/liveTranslate";
import { recommendedMaintenance } from "./lib/maintenance";
import { useServiceDraft } from "./lib/serviceDraft";
import { MaintenanceScreen } from "./components/MaintenanceScreen";
import { MaintenanceWizard } from "./components/MaintenanceWizard";
import { isVerifiedRecord } from "./lib/maintenance";
import { ProfilePhotoCropper } from "./components/ProfilePhotoCropper";
import { SessionManagement } from "./components/SessionManagement";
import { SecuritySettings } from "./components/SecuritySettings";
import { DataExport } from "./components/DataExport";
import { VehiclePassportAccess } from "./components/VehiclePassportAccess";
import {PreparationExperience,PrecisionShop} from "./components/PreparationShop";
import {UnifiedShop} from "./components/UnifiedShop";
import {VehicleIdentityFields} from "./components/VehicleIdentityFields";
import {VehicleRegistrationFields,registrationDetailsFromForm} from "./components/VehicleRegistrationFields";
import {CommercialPartner} from "./components/CommercialPartner";
import {ShopOrders} from "./components/ShopOrders";
import {AccountWallet} from "./components/AccountWallet";
import {ShopPayment} from "./components/ShopPayment";
import {translateShop} from "./lib/shopI18n";
import { TvpfProgressPanel, tvpfActivityText, tvpfBannerStatusText } from "./components/TvpfProgressPanel";
import { DemoWallet, DemoControls, DemoOrders, DemoShop } from "./components/DemoExperience";
import { PrecisionPayCard } from "./components/PrecisionPayCard";
import { DEMO_API_BASE, resetDemoEnvironment, startDemoSimulation } from "./lib/demoApi";

const baseTools: [string, string, PrecisionIconName][] = [
  ["Battery Health", "SOH, idade, km e Delta", "battery"],
  ["Custo de carregamento", "Custos e poupança", "cost"],
  ["Autonomia real", "Estimativa por condições", "range"],
  ["Buying Assistant", "Apoio à compra", "target"],
  ["Charge Session", "Tempo, energia e custo", "clock"],
  ["Garantia da bateria", "Verificar cobertura", "shield"],
];
const quickIconNames: PrecisionIconName[] = [
  "battery", "mileage", "wrench", "charge", "expense", "document", "tyres", "note",
];
const quickTypes: VehicleEvent["type"][] = [
  "battery",
  "mileage",
  "maintenance",
  "charging",
  "expense",
  "document",
  "tyres",
  "note",
];
const quickLabelsByLanguage = {
  pt: [
    "Medição da bateria",
    "Quilometragem",
    "Manutenção",
    "Carregamento",
    "Despesa",
    "Documento",
    "Pneus",
    "Nota / Evento",
  ],
  de: [
    "Batteriemessung",
    "Kilometerstand",
    "Wartung",
    "Laden",
    "Ausgabe",
    "Dokument",
    "Reifen",
    "Notiz / Ereignis",
  ],
  en: [
    "Battery measurement",
    "Mileage",
    "Maintenance",
    "Charging",
    "Expense",
    "Document",
    "Tyres",
    "Note / Event",
  ],
  fr: [
    "Mesure de batterie",
    "Kilométrage",
    "Entretien",
    "Recharge",
    "Dépense",
    "Document",
    "Pneus",
    "Note / Événement",
  ],
  it: [
    "Misurazione batteria",
    "Chilometraggio",
    "Manutenzione",
    "Ricarica",
    "Spesa",
    "Documento",
    "Pneumatici",
    "Nota / Evento",
  ],
} as const;

type TeslaIconName =
  | "battery" | "charge" | "range" | "buying" | "session"
  | "warranty" | "maintenance" | "history" | "tools" | "menu"
  | "chevronDown" | "chevronLeft" | "support" | "products" | "settings";
type ShortcutId = ToolId | "maintenance" | "history" | "support" | "garage" | "shop";

function TeslaIcon({ name }: { name: TeslaIconName }) {
  const paths: Record<TeslaIconName, React.ReactNode> = {
    battery: <><rect x="5" y="7" width="16" height="12" rx="2"/><path d="M21 10h2v6h-2M13 9l-3 5h3l-2 4 5-6h-3l2-3"/></>,
    charge: <path d="M14 2 6 13h6l-2 9 8-12h-6l2-8Z"/>,
    range: <><path d="M4 12h16M8 8l-4 4 4 4M16 8l4 4-4 4"/></>,
    buying: <><circle cx="12" cy="12" r="8"/><path d="M8 12h8M12 8v8"/></>,
    session: <><circle cx="12" cy="12" r="8"/><path d="M12 7v5l3 2"/></>,
    warranty: <><path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6l-7-3Z"/><path d="m9 12 2 2 4-5"/></>,
    maintenance: <><path d="m14 6 4-4 4 4-4 4M3 21l8-8"/><circle cx="8" cy="8" r="3"/><path d="m13 13 8 8"/></>,
    history: <><path d="M4 12a8 8 0 1 0 2-5.3L4 9"/><path d="M4 4v5h5M12 8v5l3 2"/></>,
    tools: <><path d="M4 7h16M7 3v8M4 17h16M16 13v8"/></>,
    menu: <path d="M4 7h16M4 12h16M4 17h16"/>,
    chevronDown: <path d="m5 9 7 7 7-7"/>,
    chevronLeft: <path d="m15 5-7 7 7 7"/>,
    support: <><path d="M14 6a4 4 0 0 0-5 5L3 17l4 4 6-6a4 4 0 0 0 5-5l-3 3-4-4 3-3Z"/></>,
    products: <><path d="M4 8h16v12H4zM7 8V5h10v3"/><path d="M9 12h6"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.4 1A7 7 0 0 0 15 6l-.3-2.6h-4L10.4 6a7 7 0 0 0-1.5.9l-2.4-1-2 3.4 2 1.5a7 7 0 0 0 0 2.2l-2 1.5 2 3.4 2.4-1a7 7 0 0 0 1.5.9l.3 2.6h4l.3-2.6a7 7 0 0 0 1.5-.9l2.4 1 2-3.4-2-1.5c.1-.3.1-.7.1-1Z"/></>,
  };
  return <svg className="tesla-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}
const toolDescriptions = {
  pt: [
    "SOH, idade, km e Delta",
    "Custos e poupança",
    "Estimativa por condições",
    "Apoio à compra",
    "Tempo, energia e custo",
    "Verificar cobertura",
  ],
  de: [
    "SOH, Alter, km und Delta",
    "Kosten und Einsparungen",
    "Schätzung nach Bedingungen",
    "Kaufberatung",
    "Zeit, Energie und Kosten",
    "Abdeckung prüfen",
  ],
  en: [
    "SOH, age, mileage and Delta",
    "Costs and savings",
    "Estimate by conditions",
    "Purchase support",
    "Time, energy and cost",
    "Check coverage",
  ],
  fr: [
    "SOH, âge, km et Delta",
    "Coûts et économies",
    "Estimation selon conditions",
    "Aide à l’achat",
    "Temps, énergie et coût",
    "Vérifier la couverture",
  ],
  it: [
    "SOH, età, km e Delta",
    "Costi e risparmio",
    "Stima in base alle condizioni",
    "Supporto all’acquisto",
    "Tempo, energia e costo",
    "Verifica copertura",
  ],
} as const;
const toolNames = {
  pt: [
    "Battery Health",
    "Custo de carregamento",
    "Autonomia real",
    "Buying Assistant",
    "Charge Session",
    "Garantia da bateria",
  ],
  de: [
    "Battery Health",
    "Ladekosten",
    "Reale Reichweite",
    "Buying Assistant",
    "Ladesitzung",
    "Batteriegarantie",
  ],
  en: [
    "Battery Health",
    "Charging cost",
    "Real range",
    "Buying Assistant",
    "Charge Session",
    "Battery warranty",
  ],
  fr: [
    "Battery Health",
    "Coût de recharge",
    "Autonomie réelle",
    "Buying Assistant",
    "Session de recharge",
    "Garantie batterie",
  ],
  it: [
    "Battery Health",
    "Costo di ricarica",
    "Autonomia reale",
    "Buying Assistant",
    "Sessione di ricarica",
    "Garanzia batteria",
  ],
} as const;
const TVPF_API = "https://precisionautotechnik.synology.me:8443/tvpf-php/";
const BATTERY_STUDY_API = `${TVPF_API}?action=public_battery_study`;
const DEMO_MODE = typeof window !== "undefined" && (window.location.pathname.replace(/\/+$/, "") === "/developer" || new URL(window.location.href).searchParams.get("demo") === "1");
const SUPPORT_PREVIEW_MODE = typeof window !== "undefined" && new URLSearchParams(window.location.hash.slice(1)).get("support-preview") === "1";
const DRIVE_APP = typeof window !== "undefined" ? new URL(window.location.href).searchParams.get("driveApp") : null;
if (typeof document !== "undefined" && DRIVE_APP) document.documentElement.dataset.driveApp = DRIVE_APP;
const PRODUCTION_API = String(import.meta.env.VITE_API_BASE_URL || "").replace(
  /\/$/,
  "",
);
const CONNECTED_API = SUPPORT_PREVIEW_MODE ? "preview:support" : DEMO_MODE ? DEMO_API_BASE : PRODUCTION_API;
const SHORTCUTS_KEY = SUPPORT_PREVIEW_MODE ? "precisionEV.supportPreview.shortcuts" : DEMO_MODE ? "precisionEV.demo.shortcuts" : "precisionEV.shortcuts";
const PREFERENCE_ACCOUNT_KEY = SUPPORT_PREVIEW_MODE ? "precisionEV.supportPreview.preferenceAccount" : DEMO_MODE ? "precisionEV.demo.preferenceAccount" : "precisionEV.preferenceAccount";
const TESLA_ENABLED =
  !DEMO_MODE && !SUPPORT_PREVIEW_MODE && import.meta.env.VITE_TESLA_ENABLED === "true" && Boolean(CONNECTED_API);
type TeslaStatus = "loading" | "available" | "connected" | "disabled" | "error";

async function ensureServerSession() {
  const check = await fetch(apiUrl(CONNECTED_API,"/api/v1/session"), {
    credentials: "include",
  });
  if (check.ok && (await check.json()).authenticated) return;
  const created = await fetch(apiUrl(CONNECTED_API,"/api/v1/session"), {
    method: "POST",
    credentials: "include",
  });
  if (!created.ok) throw new Error("SESSION_UNAVAILABLE");
}

async function fetchTeslaVehicles(): Promise<Vehicle[]> {
  const response = await fetch(
    apiUrl(CONNECTED_API,"/api/v1/integrations/tesla/vehicles"),
    { credentials: "include", cache: "no-store" },
  );
  if (!response.ok) throw new Error("TESLA_VEHICLES_UNAVAILABLE");
  const payload = await response.json();
  return (payload.vehicles || []).map((v: any) => ({
    id: `tesla:${v.providerVehicleId}`,
    provider: "TESLA",
    providerVehicleId: String(v.providerVehicleId),
    name: v.displayName || "Tesla",
    vin: v.vin || "",
    connectionStatus: v.connectionStatus || "UNKNOWN",
    lastUpdated: payload.updatedAt,
    events: [],
  }));
}

function batteryStudyClientKey() {
  const key = "precision-battery-study-key";
  let value = localStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    localStorage.setItem(key, value);
  }
  return value;
}

async function contributeBatteryStudy(
  vehicle: Vehicle,
  soh: number,
  delta: number,
  mileage?: number,
  language = "pt",
) {
  if (DEMO_MODE) return;
  if (!vehicle.firstRegistration || !mileage || !soh || !Number.isFinite(delta))
    return;
  try {
    await fetch(BATTERY_STUDY_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        consent: true,
        productionMonth: vehicle.firstRegistration,
        mileage,
        soh,
        delta,
        language,
        clientKey: batteryStudyClientKey(),
      }),
    });
  } catch {
    // The measurement remains saved locally even if the anonymous study is offline.
  }
}

function resizeImage(file: File, removeWhite = false): Promise<string> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 1400 / image.width);
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      const context = canvas.getContext("2d");
      context?.drawImage(image, 0, 0, canvas.width, canvas.height);
      if (removeWhite && context) {
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < pixels.data.length; i += 4) {
          const r = pixels.data[i], g = pixels.data[i + 1], b = pixels.data[i + 2];
          const light = Math.min(r, g, b), spread = Math.max(r, g, b) - light;
          if (light > 225 && spread < 24) pixels.data[i + 3] = Math.max(0, Math.round((245 - light) * 12.75));
        }
        context.putImageData(pixels, 0, 0);
      }
      resolve(canvas.toDataURL("image/webp", 0.9));
      URL.revokeObjectURL(image.src);
    };
    image.src = URL.createObjectURL(file);
  });
}

function dueMaintenance(vehicle?: Vehicle) { return vehicle ? recommendedMaintenance(vehicle) : []; }

function canManageVehicleServices(vehicle?: Vehicle) {
  return Boolean(vehicle && (vehicle.passport?.isOwner !== false || vehicle.passport?.permissions?.canManageServices));
}

function canEditVehicleDetails(vehicle?: Vehicle) {
  return Boolean(vehicle && (vehicle.passport?.isOwner !== false || vehicle.passport?.permissions?.canEditVehicle));
}

function vehicleNeedsCustomerAction(vehicle?: Vehicle) {
  if (!vehicle) return false;
  const dossier = vehicle.dossierSnapshot as Record<string, unknown> | undefined;
  if (!dossier) return /aprova|confirm|resposta|decision|freigabe/i.test(vehicle.workshopActivity || "");
  const appointment = dossier.appointment as Record<string, unknown> | undefined;
  const initial = dossier.initialDiagnostic as Record<string, unknown> | undefined;
  const quotes = Array.isArray(dossier.quotes) ? dossier.quotes as Record<string, unknown>[] : [];
  const approvals = Array.isArray(dossier.diagnosticApprovals) ? dossier.diagnosticApprovals as Record<string, unknown>[] : [];
  const payment = dossier.payment as Record<string, unknown> | undefined;
  const paymentChoice = payment?.customerChoice as Record<string, unknown> | undefined;
  const pickup = dossier.pickup as Record<string, unknown> | undefined;
  return String(appointment?.status || "").toLowerCase() === "proposed"
    || String(initial?.status || "").toLowerCase() === "pending"
    || quotes.some((item) => String(item.status || "").toLowerCase() === "pending")
    || approvals.some((item) => String(item.status || "").toLowerCase() === "pending")
    || (Number(payment?.amount || 0) > 0 && String(payment?.status || "").toLowerCase() !== "paid" && !paymentChoice?.method)
    || (String(payment?.status || "").toLowerCase() === "paid" && Boolean(pickup?.authorizedAt) && !pickup?.method);
}

function confirmedWorkshopAppointment(vehicle: Vehicle | undefined, language: Settings["language"]) {
  const dossier = vehicle?.dossierSnapshot as Record<string, unknown> | undefined;
  const appointment = dossier?.appointment as Record<string, unknown> | undefined;
  const status = String(appointment?.status || "").toLowerCase();
  if (!appointment || (status !== "confirmed" && !appointment.confirmedAt)) return undefined;
  if (["cancelled", "change_requested", "proposed"].includes(status)) return undefined;
  const date = new Date(String(appointment.scheduledAt || ""));
  if (Number.isNaN(date.getTime())) return undefined;
  const locale = { pt: "pt-PT", de: "de-CH", en: "en-GB", fr: "fr-CH", it: "it-CH" }[language];
  const day = new Intl.DateTimeFormat(locale, { day: "2-digit" }).format(date);
  const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(date).replace(".", "").toUpperCase();
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date).replace(".", "");
  const time = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  const fullDate = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  return { day, month, weekday, time, fullDate };
}

function scheduledDeliveryAppointment(vehicle: Vehicle | undefined, language: Settings["language"]) {
  if (!vehicle?.tvpfDeliveryDate || vehicle.tvpfComplete) return undefined;
  const date = new Date(`${vehicle.tvpfDeliveryDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  const locale = { pt: "pt-PT", de: "de-CH", en: "en-GB", fr: "fr-CH", it: "it-CH" }[language];
  const day = new Intl.DateTimeFormat(locale, { day: "2-digit" }).format(date);
  const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(date).replace(".", "").toUpperCase();
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date).replace(".", "");
  const time = { pt: "Entrega", de: "Übergabe", en: "Delivery", fr: "Livraison", it: "Consegna" }[language];
  const fullDate = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(date);
  return { day, month, weekday, time, fullDate };
}

function messageCenterDate(value: string, language: Settings["language"]) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = { pt: "pt-PT", de: "de-CH", en: "en-GB", fr: "fr-CH", it: "it-CH" }[language];
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

export default function App() {
  const [entryMode, setEntryMode] = useState<"checking" | "welcome" | "login" | "register" | "guest" | "app">("checking");
  const [route, setRoute] = useState<Route>(() => DRIVE_APP === "tools" ? "tools" : "home");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleSyncState, setVehicleSyncState] = useState<"idle" | "loading" | "ready" | "saving" | "error">("idle");
  const [startupNotice, setStartupNotice] = useState("");
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const [notificationPermission,setNotificationPermission]=useState<NotificationPermission|'unsupported'>(()=>'Notification' in window?Notification.permission:'unsupported');
  const preferenceAccount = useRef("");
  const [preferenceAccountId, setPreferenceAccountId] = useState("");
  const [preferenceSync, setPreferenceSync] = useState<"saved" | "saving" | "offline">("saved");
  function changePreferences(patch: Partial<Preferences>) {
    if (preferenceAccount.current && entryMode === "app") queuePreferences(preferenceAccount.current, patch);
    setSettings(current => { const next = {...current, ...patch}; saveSettings(next); return next; });
  }
  useEffect(() => {
    if (SUPPORT_PREVIEW_MODE || !preferenceAccountId || entryMode !== "app") return;
    let active = true, running = false;
    async function flush() {
      if (!active || running) return;
      running = true;
      try {
        while (active) {
          const record = readPreferences(preferenceAccountId);
          if (!Object.keys(record.pending).length) { setPreferenceSync("saved"); break; }
          setPreferenceSync("saving");
          await saveAccountPreferences(CONNECTED_API, record.pending);
          if (!active) break;
          acknowledgePreferences(preferenceAccountId, record.revision);
        }
      } catch { if (active) setPreferenceSync("offline"); }
      finally { running = false; }
    }
    void flush();
    const retry = () => { void flush(); };
    window.addEventListener("precision-preferences-changed", retry);
    window.addEventListener("online", retry);
    window.addEventListener("focus", retry);
    const timer = window.setInterval(retry, 15000);
    return () => { active = false; clearInterval(timer); window.removeEventListener("precision-preferences-changed", retry); window.removeEventListener("online", retry); window.removeEventListener("focus", retry); };
  }, [preferenceAccountId, entryMode]);
  const [demoShopOpen,setDemoShopOpen]=useState(false);
  const [shopOpen,setShopOpen]=useState(() => DRIVE_APP === "shop");
  const [demoBarCollapsed, setDemoBarCollapsed] = useState(() => DEMO_MODE);
  const [selected, setSelected] = useState<string>();
  const menuMotion=useMotionPresence(selected==='menu'?'menu':null);
  const [returnedPayment,setReturnedPayment]=useState<string|null>(()=>{const id=new URL(window.location.href).searchParams.get('paymentReturn');return id&&/^PS-[A-F0-9]{10}$/.test(id)?id:null;});
  const closeReturnedPayment=(paid=false)=>{setReturnedPayment(null);const url=new URL(window.location.href);url.searchParams.delete('paymentReturn');window.history.replaceState(null,'',url);setSelected(paid?'profile-orders':'profile-wallet');};
  const [editingVehicleId, setEditingVehicleId] = useState<string>();
  const [showVehicle, setShowVehicle] = useState(false);
  const [showVehiclePicker, setShowVehiclePicker] = useState(false);
  const [productViewId, setProductViewId] = useState<string>();
  const [inventoryOpen, setInventoryOpen] = useState(() => DRIVE_APP === "vehicles-sale");
  const [tvpfProgressVehicleId, setTvpfProgressVehicleId] = useState<string>();
  const [accountVehiclePanelId, setAccountVehiclePanelId] = useState<string>();
  const [openDraftVehicleId, setOpenDraftVehicleId] = useState<string>();
  const [openResourcesVehicleId, setOpenResourcesVehicleId] = useState<string>();
  const [openChatVehicleId, setOpenChatVehicleId] = useState<string>();
  const [messageCenterOpen, setMessageCenterOpen] = useState(false);
  const [messageCenterLoading, setMessageCenterLoading] = useState(false);
  const [messageThreads, setMessageThreads] = useState<Array<{ vehicle: Vehicle; overview: AssistanceOverview }>>([]);
  const [activeTool, setActiveTool] = useState<ToolId>();
  const [editingShortcuts, setEditingShortcuts] = useState(false);
  const [shortcutSlot, setShortcutSlot] = useState(0);
  const [supportOpen, setSupportOpen] = useState(() => DRIVE_APP === "assistance");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [supportCategory, setSupportCategory] = useState<string>();
  const [onTripVehicleId,setOnTripVehicleId] = useState<string>();
  const [maintenanceOpen, setMaintenanceOpen] = useState(() => DRIVE_APP === "maintenance");
  const [maintenanceWizardOpen, setMaintenanceWizardOpen] = useState(false);
  const [maintenanceRequestDraft, setMaintenanceRequestDraft] = useState<{ mileage: number; note: string }>();
  const [editingEvent, setEditingEvent] = useState<{ vehicleId: string; event: VehicleEvent }>();
  const [profilePhotoFile, setProfilePhotoFile] = useState<File>();
  const [profileResetOpen, setProfileResetOpen] = useState(false);
  const [profileResetBusy, setProfileResetBusy] = useState(false);
  const [profileResetNotice, setProfileResetNotice] = useState("");
  const [profileSignOutBusy, setProfileSignOutBusy] = useState(false);
  const [accountDeleteOpen, setAccountDeleteOpen] = useState(false);
  const [accountDeleteBusy, setAccountDeleteBusy] = useState(false);
  const [accountDeleteNotice, setAccountDeleteNotice] = useState("");
  const [shortcuts, setShortcuts] = useState<ShortcutId[]>(() => {
    const defaults: ShortcutId[] = ["battery", "charging", "maintenance", "history"];
    try {
      const saved = JSON.parse(
        localStorage.getItem(SHORTCUTS_KEY) ||
          '["battery","charging","session","warranty"]',
      ) as ShortcutId[];
      const valid = saved.filter((item, index) =>
        ["battery", "charging", "range", "buying", "session", "warranty", "maintenance", "history", "support", "garage", "shop"].includes(item) && saved.indexOf(item) === index,
      );
      return [...valid, ...defaults.filter((item) => !valid.includes(item))].slice(0, 4);
    } catch {
      return defaults;
    }
  });
  const holdTimer = useRef<number | undefined>(undefined);
  const suppressShortcutClick = useRef(false);
  const draggedShortcut = useRef<ShortcutId | undefined>(undefined);
  const vehiclesHydrated = useRef(false);
  const skipNextVehicleSave = useRef(true);
  const [quickType, setQuickType] = useState<VehicleEvent["type"]>();
  const [teslaStatus, setTeslaStatus] = useState<TeslaStatus>(
    TESLA_ENABLED ? "loading" : "disabled",
  );
  const {vehicle:primary,select:selectVehicle} = useActiveVehicle(vehicles,`${DEMO_MODE?"demo":"account"}:${settings.profileEmail?.toLowerCase()||"session"}`);
  const activeVehicleId=primary?.id;
  const vehicleImageFor = (vehicle: Vehicle) => vehicleImage(vehicle, Math.max(0, vehicles.findIndex((item) => item.id === vehicle.id)));
  const recoverVehicleImage = (event: React.SyntheticEvent<HTMLImageElement>, vehicle: Vehicle) => {
    const image = event.currentTarget;
    const fallback = vehicleImage({ photo: undefined }, Math.max(0, vehicles.findIndex((item) => item.id === vehicle.id)));
    if (image.getAttribute("src") === fallback) return;
    image.src = fallback;
    image.classList.add("default-vehicle-image");
  };
  const tvpfProgressVehicle = useMemo(
    () => vehicles.find((vehicle) => vehicle.id === tvpfProgressVehicleId),
    [vehicles, tvpfProgressVehicleId],
  );
  const homeDraft = useServiceDraft(`${CONNECTED_API}:${primary?.id || ""}`);
  const maintenanceDue = useMemo(() => dueMaintenance(primary), [primary]);
  const quickLabels = quickLabelsByLanguage[settings.language];
  const tools: [string, string, PrecisionIconName][] = baseTools.map((item, index) => [
    toolNames[settings.language][index],
    toolDescriptions[settings.language][index],
    item[2],
  ]);
  const preparationCopy = {
    pt: { eyebrow: "PREPARAÇÃO PRECISION", fallback: "Preparação em curso", progress: "concluído" },
    de: { eyebrow: "PRECISION AUFBEREITUNG", fallback: "Aufbereitung läuft", progress: "abgeschlossen" },
    en: { eyebrow: "PRECISION PREPARATION", fallback: "Preparation in progress", progress: "complete" },
    fr: { eyebrow: "PRÉPARATION PRECISION", fallback: "Préparation en cours", progress: "terminé" },
    it: { eyebrow: "PREPARAZIONE PRECISION", fallback: "Preparazione in corso", progress: "completato" },
  }[settings.language];
  useLiveTranslation(settings.language);
  useEffect(() => {
    if (!messageCenterOpen) return;
    const scrollY = window.scrollY;
    const previous = { overflow: document.body.style.overflow, position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = previous.overflow;
      document.body.style.position = previous.position;
      document.body.style.top = previous.top;
      document.body.style.width = previous.width;
      window.scrollTo(0, scrollY);
    };
  }, [messageCenterOpen]);

  const reloadVehicles = useCallback(async () => {
    const incoming = await loadAccountVehicles(CONNECTED_API);
    vehiclesHydrated.current = true;
    skipNextVehicleSave.current = true;
    saveVehicles(incoming);
    setVehicles(incoming);
  }, []);

  useEffect(() => DEMO_MODE ? startDemoSimulation(() => { void reloadVehicles(); void refreshMessagesRef.current(); window.dispatchEvent(new Event("precision-demo-updated")); }) : undefined, [reloadVehicles]);

  async function signOut() {
    if (profileSignOutBusy) return;
    setProfileSignOutBusy(true);
    try {
      await logoutAccount(CONNECTED_API);
    } finally {
      preferenceAccount.current = "";
      setPreferenceAccountId("");
      clearLocalVehicles();
      vehiclesHydrated.current = false;
      skipNextVehicleSave.current = true;
      setVehicles([]);
      setVehicleSyncState("idle");
      setSelected(undefined);
      setEntryMode("welcome");
      setProfileSignOutBusy(false);
    }
  }

  const refreshMessagesRef = useRef<() => Promise<void>>(async () => {});
  const messageVehicleIds = vehicles.map(v => v.id).sort().join("|");
  const messageVehiclesRef = useRef(vehicles);
  messageVehiclesRef.current = vehicles;
  useEffect(() => {
    let active = true, running = false;
    setMessageThreads([]);
    const refresh = async () => {
      if (!active || running || entryMode !== "app" || document.visibilityState !== "visible") return;
      running = true;
      try {
        const currentVehicles = messageVehiclesRef.current;
        const results = await Promise.allSettled(currentVehicles.map(async vehicle => ({vehicle, overview: await loadVehicleAssistance(CONNECTED_API, vehicle.id)})));
        if (!active) return;
        setMessageThreads(previous => currentVehicles.flatMap((vehicle, index) => {
          const result = results[index];
          const prior = previous.find(row => row.vehicle.id === vehicle.id);
          if (result.status !== "fulfilled") return prior ? [prior] : [];
          const row = result.value;
          if (!row.overview.thread && !row.overview.messages.length) return [];
          // Do not regress a receipt if a GET started before the read acknowledgement.
          if (prior?.overview.thread?.id === row.overview.thread?.id) row.overview.messages = row.overview.messages.map(message => ({...message, readAt: message.readAt || prior?.overview.messages.find(m => m.id === message.id)?.readAt}));
          return [row];
        }).sort((a,b) => String(b.overview.thread?.updatedAt || "").localeCompare(String(a.overview.thread?.updatedAt || ""))));
      } finally { running = false; }
    };
    refreshMessagesRef.current = refresh;
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60000);
    const wake = () => void refresh();
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
    return () => { active=false; clearInterval(timer);window.removeEventListener("focus",wake);document.removeEventListener("visibilitychange",wake); };
  }, [entryMode, messageVehicleIds, settings.profileEmail]);
  useEffect(() => {
    const acknowledged = (event: Event) => {
      const {vehicleId,threadId,messageIds} = (event as CustomEvent<{vehicleId:string;threadId:string;messageIds:string[]}>).detail;
      setMessageThreads(rows => rows.map(row => row.vehicle.id !== vehicleId || row.overview.thread?.id !== threadId ? row : {...row,overview:{...row.overview,messages:row.overview.messages.map(message => messageIds.includes(message.id) ? {...message,readAt:message.readAt || new Date().toISOString()} : message)}}));
    };
    window.addEventListener("precision-chat-read", acknowledged);
    return () => window.removeEventListener("precision-chat-read", acknowledged);
  }, []);
  async function openMessageCenter() {
    setMessageCenterOpen(true);
    setMessageCenterLoading(true);
    try { await refreshMessagesRef.current(); } finally { setMessageCenterLoading(false); }
  }

  function applyAccountProfile(account: AccountProfile) {
    const fullName = [account.firstName, account.lastName].filter(Boolean).join(" ");
    const initialize = preferenceAccount.current !== account.id;
    preferenceAccount.current = account.id;
    setPreferenceAccountId(account.id);
    try { localStorage.setItem(PREFERENCE_ACCOUNT_KEY, JSON.stringify({id:account.id,email:account.email})); } catch {}
    setSettings((current) => ({
      ...current,
      ...(initialize ? hydratePreferences(account, current) : {}),
      profileName: fullName,
      profileEmail: account.email,
      profilePhone: account.phone || "",
      profileAddress: account.address || "",
      profilePhoto: account.profilePhoto || "",
      notificationsEnabled: account.notificationsEnabled,
      emailVerified: account.emailVerified,
    }));
  }

  function recoverFromServiceOutage() {
    const cached = getAuthToken() ? loadVehicles() : [];
    setVehicleSyncState("error");
    setStartupNotice("A ligação ao serviço está temporariamente indisponível. Os dados guardados neste dispositivo continuam acessíveis e pode tentar novamente dentro de alguns minutos.");
    if (!cached.length) {
      setEntryMode("welcome");
      return;
    }
    vehiclesHydrated.current = true;
    skipNextVehicleSave.current = true;
    try {
      const account = JSON.parse(localStorage.getItem(PREFERENCE_ACCOUNT_KEY) || "null");
      if (account?.id && account.email === settings.profileEmail) {
        preferenceAccount.current = account.id;
        setPreferenceAccountId(account.id);
      }
    } catch {}
    setVehicles(cached);
    setEntryMode("app");
  }

  async function openAuthenticatedApp(existingSession?: { authenticated: true; account: AccountProfile }) {
    setVehicleSyncState("loading");
    const session = existingSession || await restoreAccountSession(CONNECTED_API);
    if (!session) throw new Error("SESSION_UNAVAILABLE");
    applyAccountProfile(session.account);
    let incoming = await loadAccountVehicles(CONNECTED_API);
    if (!incoming.length) {
      const legacyVehicles = loadVehicles();
      if (legacyVehicles.length) {
        await saveAccountVehicles(CONNECTED_API, legacyVehicles);
        clearLocalVehicles();
        incoming = legacyVehicles;
      }
    }
    vehiclesHydrated.current = true;
    saveVehicles(incoming);
    setVehicles(incoming);
    setVehicleSyncState("ready");
    setStartupNotice("");
    setEntryMode("app");
    if (!DEMO_MODE && !SUPPORT_PREVIEW_MODE && "Notification" in window && Notification.permission === "default") {
      void Notification.requestPermission().then((permission) => {
        const enabled = permission === "granted";
        setSettings((current) => ({ ...current, notificationsEnabled: enabled }));
        void saveAccountProfile(CONNECTED_API, {
          fullName: [session.account.firstName, session.account.lastName].filter(Boolean).join(" "),
          phone: session.account.phone || "",
          address: session.account.address || "",
          profilePhoto: session.account.profilePhoto || "",
          notificationsEnabled: enabled,
        }).catch(() => undefined);
      });
    }
  }
  useEffect(() => {
    let active = true;
    const restore = async () => {
      const url = new URL(window.location.href), handoff = url.searchParams.get("driveHandoff");
      if (handoff) {
        try { await exchangeDriveHandoff(CONNECTED_API, handoff); }
        finally { url.searchParams.delete("driveHandoff"); window.history.replaceState(null, "", url); }
      }
      return restoreAccountSession(CONNECTED_API);
    };
    void restore().then(async (session) => {
      if (!active) return;
      if (!session) return setEntryMode("welcome");
      applyAccountProfile(session.account);
      try { await openAuthenticatedApp(session); } catch { if (active) recoverFromServiceOutage(); }
    }).catch(() => {
      if (!active) return;
      recoverFromServiceOutage();
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (entryMode !== "app" || !vehiclesHydrated.current) return;
    saveVehicles(vehicles);
    if (SUPPORT_PREVIEW_MODE) return;
    if (skipNextVehicleSave.current) {
      skipNextVehicleSave.current = false;
      return;
    }
    setVehicleSyncState("saving");
    const timer = window.setTimeout(() => {
      void saveAccountVehicles(CONNECTED_API, vehicles)
        .then(() => setVehicleSyncState("ready"))
        .catch(() => setVehicleSyncState("error"));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [vehicles, entryMode]);
  useEffect(() => {
    if (entryMode !== "app") return;
    const url = new URL(location.href), token = url.searchParams.get("vehicleInvite");
    if (!token) return;
    url.searchParams.delete("vehicleInvite");
    history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
    void acceptVehicleDriverInvite(CONNECTED_API, token)
      .then(async ({ vehicleId }) => { await reloadVehicles(); setProductViewId(vehicleId); setSelected("products"); })
      .catch((error) => alert(error instanceof Error && error.message === "INVITE_EMAIL_MISMATCH" ? "Este convite foi criado para outra conta." : "Este convite não é válido ou já expirou."));
  }, [entryMode]);
  useEffect(
    () =>
      localStorage.setItem(SHORTCUTS_KEY, JSON.stringify(shortcuts)),
    [shortcuts],
  );
  useEffect(() => {
    saveSettings(settings);
    document.documentElement.lang = settings.language;
    document.documentElement.dataset.theme = settings.theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", settings.theme === "dark" ? "#0b0d10" : "#ffffff");
  }, [settings]);
  useEffect(()=>{const update=()=>setNotificationPermission('Notification' in window?Notification.permission:'unsupported');window.addEventListener('focus',update);document.addEventListener('visibilitychange',update);return()=>{window.removeEventListener('focus',update);document.removeEventListener('visibilitychange',update);};},[]);
  useEffect(() => {
    if (DEMO_MODE || SUPPORT_PREVIEW_MODE || !maintenanceDue.length || !("Notification" in window) || Notification.permission !== "granted") return;
    const item = maintenanceDue[0];
    const key = `precisionEV.maintenanceNotice.${primary?.id}.${item.id}.${new Date().toISOString().slice(0, 10)}`;
    if (localStorage.getItem(key)) return;
    const target = [
      item.nextDueDate ? new Date(`${item.nextDueDate.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-PT") : "",
      item.nextDueMileage != null ? `${Number(item.nextDueMileage).toLocaleString("pt-PT")} km` : "",
    ].filter(Boolean).join(" ou ");
    const notification: NotificationOptions = {
      body: `${item.title} · ${target || primary?.nickname || primary?.name || "Veículo"}`,
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      tag: `maintenance-${primary?.id}-${item.id}`,
      data: { url: "/" },
    };
    const fallback = () => { try { new Notification("Próxima manutenção", notification); } catch { /* service-worker notification is preferred */ } };
    void navigator.serviceWorker?.ready.then(registration => registration.showNotification("Próxima manutenção", notification)).catch(fallback);
    localStorage.setItem(key, "1");
  }, [maintenanceDue, primary]);
  useEffect(() => {
    const tvpfTokens = Array.from(new Set(vehicles.map((vehicle) => vehicle.tvpfToken || "").filter(Boolean)));
    if (SUPPORT_PREVIEW_MODE || entryMode !== "app" || (!tvpfTokens.length && !primary?.workshopActive)) return;
    let active = true;
    const refresh = async () => {
      if (document.visibilityState !== "visible") return;
      await Promise.allSettled([
        ...(primary?.workshopActive ? [loadVehicleAssistance(CONNECTED_API, primary.id)] : []),
        ...tvpfTokens.map((token) => linkTvpfVehicle(CONNECTED_API, token)),
      ]);
      try {
        const incoming = await loadAccountVehicles(CONNECTED_API);
        if (active) { skipNextVehicleSave.current = true; saveVehicles(incoming); setVehicles(incoming); }
      } catch { /* keep the last known vehicle state while offline */ }
    };
    const refreshWhenVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    void refresh();
    const timer = window.setInterval(refresh, 300000);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [entryMode, primary?.id, primary?.workshopActive, vehicles.map((vehicle) => vehicle.tvpfToken || "").join("|")]);
  useEffect(() => {
    if (SUPPORT_PREVIEW_MODE || entryMode !== "app" || !vehiclesHydrated.current) return;
    const recoverable = vehicles.filter((vehicle) => vehicle.dossierLinkedAt && !vehicle.workshopActive);
    if (!recoverable.length) return;
    let active = true;
    void Promise.allSettled(recoverable.map((vehicle) => loadVehicleAssistance(CONNECTED_API, vehicle.id))).then(async () => {
      const incoming = await loadAccountVehicles(CONNECTED_API);
      if (active) { skipNextVehicleSave.current = true; setVehicles(incoming); }
    });
    return () => { active = false; };
  }, [entryMode, vehicles.map((vehicle) => `${vehicle.id}:${vehicle.dossierLinkedAt || ""}:${vehicle.workshopActive ? 1 : 0}`).join("|")]);
  useEffect(() => {
    if (SUPPORT_PREVIEW_MODE || entryMode !== "app" || !vehiclesHydrated.current) return;
    let active = true;
    const url = new URL(location.href);
    const incomingToken = url.searchParams.get("tvpf")?.trim() || "";
    const incomingDossierToken = url.searchParams.get("dossier")?.trim() || "";
    void (async () => {
      if (incomingDossierToken) {
        try {
          const linked = await linkDossierVehicle(CONNECTED_API, incomingDossierToken);
          url.searchParams.delete("dossier");
          history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
          await reloadVehicles();
          setRoute("home");
          setSelected(undefined);
          setOpenResourcesVehicleId(linked.vehicleId);
          setTvpfProgressVehicleId(linked.vehicleId);
        } catch (error) {
          const message = error instanceof Error ? error.message : "";
          alert(message === "EMAIL_VERIFICATION_REQUIRED" ? "Confirme primeiro o e-mail da sua conta. Depois volte a abrir o link do serviço." : message === "DOSSIER_CUSTOMER_MISMATCH" ? "Utilize a conta com o e-mail indicado à oficina ou contacte a receção para confirmar a titularidade." : message === "DOSSIER_VEHICLE_NOT_FOUND" ? "Não encontramos na sua conta o veículo deste dossiê. Confirme se o VIN está associado à conta Precision EV." : "Este link do Precision Dossier é inválido ou já expirou.");
        }
      }
      const currentVehicles = await loadAccountVehicles(CONNECTED_API);
      const tokens = Array.from(new Set([
        ...(incomingToken ? [incomingToken] : []),
        ...currentVehicles.map((vehicle) => vehicle.tvpfToken || "").filter(Boolean),
      ]));
      if (!tokens.length) return;
      const results = await Promise.allSettled(
        tokens.map((token) => linkTvpfVehicle(CONNECTED_API, token)),
      );
      if (!active) return;
      const incomingIndex = incomingToken ? tokens.indexOf(incomingToken) : -1;
      const incomingResult = incomingIndex >= 0 ? results[incomingIndex] : undefined;
      if (incomingToken && incomingResult?.status === "fulfilled") {
        url.searchParams.delete("tvpf");
        url.searchParams.delete("claim");
        history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
        setRoute("home");
        setSelected(undefined);
      } else if (incomingToken) {
        const reason=incomingResult?.status==="rejected"?String(incomingResult.reason?.message||""):"";
        alert(reason==="TVPF_RECIPIENT_VERIFICATION_REQUIRED" ? "Para guardar este veículo, confirme o email da sua conta e peça à Precision para indicar esse mesmo email como comprador no percurso TVPF. Pode continuar a acompanhar pelo link sem conta." : reason==="TVPF_VEHICLE_ALREADY_LINKED" ? "Este veículo já está associado a outra conta. Contacte a Precision para confirmar a titularidade." : "Não foi possível adicionar este veículo. Confirme se o link TVPF ainda está ativo.");
      }
      const refreshedVehicles = await loadAccountVehicles(CONNECTED_API);
      if (active) setVehicles(refreshedVehicles);
    })().catch(() => {
      if (active && incomingToken) alert("A ligação ao TVPF está temporariamente indisponível. Tenta novamente.");
    });
    return () => { active = false; };
  }, [entryMode]);
  useEffect(() => {
    if (!TESLA_ENABLED) return;
    const url = new URL(location.href),
      returned = url.searchParams.get("tesla");
    ensureServerSession()
      .then(() =>
        fetch(apiUrl(CONNECTED_API,"/api/v1/integrations/tesla/status"), {
          credentials: "include",
        }),
      )
      .then(async (response) => {
        if (!response.ok) throw new Error("STATUS_UNAVAILABLE");
        const status = await response.json();
        setTeslaStatus(status.connected ? "connected" : "available");
        if (status.connected || returned === "connected") {
          const incoming = await fetchTeslaVehicles();
          setVehicles((current) => {
            const manual = current.filter((v) => v.provider !== "TESLA"),
              hadPrimary = manual.some((v) => v.primary);
            return [
              ...manual,
              ...incoming.map((v, i) => ({
                ...v,
                primary: !hadPrimary && i === 0,
              })),
            ];
          });
        }
        if (returned) {
          url.searchParams.delete("tesla");
          history.replaceState(
            null,
            "",
            url.pathname + (url.search || "") + url.hash,
          );
          setRoute("garage");
        }
      })
      .catch(() => setTeslaStatus("error"));
  }, []);

  if (entryMode === "checking") return <main className="precision-auth-loading"><img src={settings.theme === "dark" ? "./precision-logo-white.png" : "./precision-logo-black.png"} alt="Precision Autotechnik" /><span aria-label="A carregar" /></main>;
  if (entryMode === "welcome" || entryMode === "login" || entryMode === "register") return <>{startupNotice && <div className="precision-startup-notice" role="alert"><span>{startupNotice}</span><button type="button" onClick={() => location.reload()}>Tentar novamente</button></div>}<AccountEntry key={entryMode} initialMode={entryMode === "login" ? "login-email" : entryMode === "register" ? "register-profile" : "welcome"} initialLanguage={settings.language} apiBase={CONNECTED_API} onLanguage={(language) => setSettings((current) => ({ ...current, language }))} onGuest={() => setEntryMode("guest")} onAuthenticated={openAuthenticatedApp} /></>;
if (entryMode === "guest") return route === "tools" ? <main className="guest-tools-shell">{activeTool ? <IntegratedTools key={activeTool} tool={activeTool} settings={settings} apiBase={CONNECTED_API} onBack={() => setActiveTool(undefined)}/> : <ToolsCatalog language={settings.language} onOpen={setActiveTool} onBack={()=>setRoute("home")}/>}</main> : <GuestLanding language={settings.language} apiBase={CONNECTED_API} onTools={() => setRoute("tools")} onSignIn={() => setEntryMode("login")} onCreateAccount={() => setEntryMode("register")} />;

  async function connectTesla() {
    if (!TESLA_ENABLED) return;
    try {
      await ensureServerSession();
      location.assign(apiUrl(CONNECTED_API,"/api/v1/integrations/tesla/connect"));
    } catch {
      setTeslaStatus("error");
    }
  }

  function addEvent(type: VehicleEvent["type"]) {
    if (!primary) {
      setRoute("garage");
      return;
    }
    if (!canManageVehicleServices(primary)) return alert("O proprietário não autorizou a gestão de serviços para este condutor.");
    setQuickType(type);
  }

  function saveIntegratedBattery(soh: number, delta: number, mileage: number, production?: string) {
    if (!primary || !soh || !canManageVehicleServices(primary)) return;
    const date = new Date().toISOString().slice(0, 10),
      measurement = {
        id: crypto.randomUUID(),
        date,
        soh,
        delta,
        mileage,
        source: "USER_REPORTED" as const,
      };
    setVehicles((list) =>
      list.map((v) =>
        v.id === primary.id
          ? {
              ...v,
              soh,
              delta,
              mileage,
              firstRegistration: production || v.firstRegistration,
              batteryMeasurements: [
                measurement,
                ...(v.batteryMeasurements || []),
              ],
              events: [
                {
                  id: crypto.randomUUID(),
                  type: "battery",
                  title: `SOH ${soh}% · Delta ${delta} mV`,
                  date,
                  odometer: mileage,
                  source: "USER_REPORTED",
                },
                ...v.events,
              ],
              lastUpdated: new Date().toISOString(),
            }
          : v,
      ),
    );
    void contributeBatteryStudy(
      primary,
      soh,
      delta,
      mileage,
      settings.language,
    );
  }

  const shortcutInfo: Record<ShortcutId, [TeslaIconName, string]> = {
    fuel: ["charge", "Combustível"],
    battery: ["battery", "Bateria"],
    charging: ["charge", "Custos"],
    range: ["range", "Autonomia"],
    buying: ["buying", "Comprar"],
    session: ["session", "Carregar"],
    warranty: ["warranty", "Garantia"],
    maintenance: ["maintenance", "Manutenção"],
    history: ["history", "Histórico"],
    support: ["support", "Assistência"],
    garage: ["products", "Veículos"],
    shop: ["products", "Shop"],
  };
  function openTool(tool: ToolId) {
    if (tool === "buying") { setActiveTool(undefined); setInventoryOpen(true); return; }
    setActiveTool(tool);
    setRoute("tools");
  }
  function startShortcutHold() {
    stopShortcutHold();
    suppressShortcutClick.current = false;
    holdTimer.current = window.setTimeout(() => {
      navigator.vibrate?.(80);
      suppressShortcutClick.current = true;
      setEditingShortcuts(true);
    }, 2200);
  }
  function stopShortcutHold() {
    if (holdTimer.current) window.clearTimeout(holdTimer.current);
    holdTimer.current = undefined;
  }

  function openShortcut(id: ShortcutId) {
    if (id === "maintenance") return canManageVehicleServices(primary) ? setMaintenanceOpen(true) : alert("O proprietário não autorizou a gestão de serviços para este condutor.");
    if (id === "history") return primary && openVehicle(primary);
    if (id === "support") return setSupportOpen(true);
    if (id === "garage") return setShowVehiclePicker(true);
    if(id==="shop"&&DEMO_MODE){setSelected(undefined);setDemoShopOpen(true);return;}
    if (id === "shop") return setShopOpen(true);
    openTool(id);
  }
  function replaceShortcut(slot: number, tool: ShortcutId) {
    setShortcuts((current) => {
      const existing = current.indexOf(tool);
      const next = [...current];
      if (existing >= 0 && existing !== slot) next[existing] = current[slot];
      next[slot] = tool;
      return next;
    });
    setShortcutSlot(slot);
  }

  function removeVehicleEvent(vehicleId: string, eventId: string) {
    const vehicle = vehicles.find((item) => item.id === vehicleId);
    if (!canManageVehicleServices(vehicle)) return alert("Não tem permissão para alterar o histórico deste veículo.");
    if (!confirm("Eliminar este registo do histórico?")) return;
    setVehicles((current) => current.map((vehicle) => vehicle.id === vehicleId
      ? { ...vehicle, events: vehicle.events.filter((event) => event.id !== eventId), lastUpdated: new Date().toISOString() }
      : vehicle));
  }

  function openVehicle(vehicle: Vehicle) {
    setEditingVehicleId(undefined);
    setSelected(vehicle.id);
    if (!vehicle.tvpfToken) return;
    void linkTvpfVehicle(CONNECTED_API, vehicle.tvpfToken)
      .then(() => reloadVehicles())
      .catch(() => undefined);
  }

  function recordQuick(form: HTMLFormElement) {
    if (!primary || !quickType || !canManageVehicleServices(primary)) return;
    const f = new FormData(form),
      date = String(f.get("date") || new Date().toISOString().slice(0, 10)),
      odometer = Number(f.get("odometer")) || primary.mileage;
    if (quickType === "battery") {
      const soh = Number(f.get("soh")),
        delta = Number(f.get("delta"));
      if (!soh || Number.isNaN(delta)) return;
      const measurement = {
        id: crypto.randomUUID(),
        date,
        soh,
        delta,
        mileage: odometer,
        source: "USER_REPORTED" as const,
        notes: String(f.get("notes") || ""),
      };
      setVehicles((list) =>
        list.map((v) =>
          v.id === primary.id
            ? {
                ...v,
                soh,
                delta,
                mileage: odometer,
                batteryMeasurements: [
                  measurement,
                  ...(v.batteryMeasurements || []),
                ],
                events: [
                  {
                    id: crypto.randomUUID(),
                    type: "battery",
                    title: `SOH ${soh}% · Delta ${delta} mV`,
                    date,
                    odometer,
                    source: "USER_REPORTED",
                  },
                  ...v.events,
                ],
                lastUpdated: new Date().toISOString(),
              }
            : v,
        ),
      );
      void contributeBatteryStudy(
        primary,
        soh,
        delta,
        odometer,
        settings.language,
      );
    } else {
      const title = String(
          f.get("title") || quickLabels[quickTypes.indexOf(quickType)],
        ),
        amount = Number(f.get("amount")) || undefined;
      const event: VehicleEvent = {
        id: crypto.randomUUID(),
        type: quickType,
        title,
        date,
        odometer,
        amount,
        source: "USER_REPORTED",
      };
      setVehicles((list) =>
        list.map((v) =>
          v.id === primary.id
            ? {
                ...v,
                mileage: quickType === "mileage" ? odometer : v.mileage,
                events: [event, ...v.events],
                lastUpdated: new Date().toISOString(),
              }
            : v,
        ),
      );
    }
    setQuickType(undefined);
    setRoute("home");
  }

  async function shareVehicle(vehicle: Vehicle) {
    const text = `${vehicle.name}${vehicle.variant ? ` ${vehicle.variant}` : ""}\n${vehicle.mileage ? `${vehicle.mileage.toLocaleString("pt-PT")} km\n` : ""}${vehicle.soh ? `Battery Health: ${vehicle.soh}%\n` : ""}Precision EV`;
    if (navigator.share)
      await navigator.share({ title: vehicle.name, text, url: location.href });
    else await navigator.clipboard.writeText(text);
  }

  const firstDriveCopy = {
    pt: { eyebrow: "A TUA GARAGEM ELÉTRICA", title: "Tudo o que o teu EV precisa.", accent: "Tudo numa só app.", text: "Acompanha a bateria, planeia a manutenção, regista carregamentos e guarda todos os documentos.", simple: "Simples. Gratuito. Sempre contigo.", add: "Adicionar o meu carro", explore: "Explorar ferramentas", private: "Os teus dados permanecem privados e seguros.", battery: "Estado da bateria", batterySub: "SOH, Delta e muito mais.", maintenance: "Manutenção", maintenanceSub: "Planeia e não falhes revisões.", charging: "Carregamentos", chargingSub: "Sessões, custos e estatísticas.", documents: "Documentos", documentsSub: "Tudo organizado num só lugar." },
    de: { eyebrow: "DEINE ELEKTRISCHE GARAGE", title: "Alles, was dein EV braucht.", accent: "Alles in einer App.", text: "Batterie überwachen, Wartung planen, Ladevorgänge erfassen und Dokumente sicher aufbewahren.", simple: "Einfach. Kostenlos. Immer dabei.", add: "Mein Auto hinzufügen", explore: "Tools entdecken", private: "Deine Daten bleiben privat und sicher.", battery: "Batteriezustand", batterySub: "SOH, Delta und mehr.", maintenance: "Wartung", maintenanceSub: "Planen und keinen Service verpassen.", charging: "Laden", chargingSub: "Sitzungen, Kosten und Statistiken.", documents: "Dokumente", documentsSub: "Alles geordnet an einem Ort." },
    en: { eyebrow: "YOUR ELECTRIC GARAGE", title: "Everything your EV needs.", accent: "All in one app.", text: "Track battery health, plan maintenance, manage charging and keep every document together.", simple: "Simple. Free. Always with you.", add: "Add my car", explore: "Explore tools", private: "Your data stays private and secure.", battery: "Battery health", batterySub: "Monitor SOH, Delta and more.", maintenance: "Maintenance", maintenanceSub: "Plan, track and never miss service.", charging: "Charging", chargingSub: "Log sessions, costs and stats.", documents: "Documents", documentsSub: "Keep everything in one place." },
    fr: { eyebrow: "VOTRE GARAGE ÉLECTRIQUE", title: "Tout ce dont votre EV a besoin.", accent: "Tout dans une seule app.", text: "Suivez la batterie, planifiez l’entretien, gérez les recharges et conservez tous vos documents.", simple: "Simple. Gratuit. Toujours avec vous.", add: "Ajouter ma voiture", explore: "Découvrir les outils", private: "Vos données restent privées et sécurisées.", battery: "Santé batterie", batterySub: "SOH, Delta et bien plus.", maintenance: "Entretien", maintenanceSub: "Planifiez et ne manquez aucun service.", charging: "Recharge", chargingSub: "Sessions, coûts et statistiques.", documents: "Documents", documentsSub: "Tout organisé au même endroit." },
    it: { eyebrow: "IL TUO GARAGE ELETTRICO", title: "Tutto ciò che serve al tuo EV.", accent: "Tutto in una sola app.", text: "Controlla la batteria, pianifica la manutenzione, registra le ricariche e conserva tutti i documenti.", simple: "Semplice. Gratuita. Sempre con te.", add: "Aggiungi la mia auto", explore: "Scopri gli strumenti", private: "I tuoi dati restano privati e sicuri.", battery: "Salute batteria", batterySub: "SOH, Delta e molto altro.", maintenance: "Manutenzione", maintenanceSub: "Pianifica e non saltare i controlli.", charging: "Ricarica", chargingSub: "Sessioni, costi e statistiche.", documents: "Documenti", documentsSub: "Tutto ordinato in un unico posto." },
  }[settings.language];

  return (
    <div className={`app-shell${DEMO_MODE ? " developer-demo-shell" : ""}`} data-release={APP_RELEASE} onClickCapture={event=>{if(event.target instanceof Element&&event.target.closest('a[href*="/category/ev-parts"]')){event.preventDefault();event.stopPropagation();setSelected(undefined);if(DEMO_MODE)setDemoShopOpen(true);else setShopOpen(true);}}}>
      {DEMO_MODE&&demoShopOpen&&<DemoShop language={settings.language} onClose={()=>setDemoShopOpen(false)}/>}
      {shopOpen&&<UnifiedShop language={settings.language} theme={settings.theme} vehicle={entryMode==='app'?primary:undefined} onClose={()=>setShopOpen(false)}/>}
      {DEMO_MODE && <aside className={`developer-demo-bar${demoBarCollapsed ? " collapsed" : ""}`} role="status">
        {demoBarCollapsed ? <button type="button" className="developer-demo-peek" aria-label="Mostrar faixa da demo" onClick={() => setDemoBarCollapsed(false)}><span>DEMO</span><b>←</b></button> : <>
          <span><b>DEMO PRIVADA · DEVELOPERS</b><small>Dados fictícios e ferramentas de simulação, separados da aplicação dos clientes</small></span>
          <span className="developer-demo-actions"><button type="button" className="developer-demo-collapse" aria-label="Recolher faixa da demo" title="Recolher" onClick={() => setDemoBarCollapsed(true)}>→</button><DemoControls /><button type="button" onClick={() => { if(confirm("Repor apenas os dados fictícios da demo?")){resetDemoEnvironment(); location.reload();} }}>Repor cenário</button><button type="button" className="secondary" onClick={() => { sessionStorage.removeItem("precisionEV.developerToken"); sessionStorage.removeItem("precisionEV.developerFallback"); sessionStorage.removeItem("precisionEV.localDeveloper"); location.assign("/"); }}>Sair da demo</button></span>
        </>}
      </aside>}
      {startupNotice && <div className="precision-startup-notice in-app" role="status"><span>{startupNotice}</span><button type="button" onClick={() => location.reload()}>Tentar novamente</button></div>}
      <main>
        {route !== "home" && route !== "tools" && !activeTool && (
          <button className="screen-back" onClick={() => setRoute("home")} aria-label="Voltar">
            <TeslaIcon name="chevronLeft" />
          </button>
        )}
        {route === "home" && (
          <section className={`screen home-screen ${!primary ? "first-use-screen" : ""}`}>
            {primary ? (
              <HomeVehiclePager vehicles={vehicles} activeId={primary.id} onSelect={selectVehicle} renderImage={vehicle=><img src={vehicleImageFor(vehicle)} onError={event=>recoverVehicleImage(event,vehicle)} alt="" className={vehicle.photo?undefined:"default-vehicle-image"}/>}>{primary=>{
                const maintenanceDue=dueMaintenance(primary),primaryNeedsAction=vehicleNeedsCustomerAction(primary),primaryAppointment=confirmedWorkshopAppointment(primary,settings.language)||scheduledDeliveryAppointment(primary,settings.language);
                return <>
                <div className="tesla-home-head">
                  <div className="vehicle-title-stack">
                    <button
                      className="vehicle-switch"
                      onClick={() => setShowVehiclePicker(true)}
                    >
                      <b>{primary.nickname || primary.registration || "Meu carro"}</b>
                      <span className="vehicle-chevron"><TeslaIcon name="chevronDown" /></span>
                      <small>{primary.name}</small>
                    </button>
                    {primary.precisionCertified && <div className="precision-verified-badge home-verified"><span>◆</span> Precision Verified</div>}
                  </div>
                  <div className="tesla-home-actions">
                    {primary.id===activeVehicleId && homeDraft.items.length > 0 && <button className="service-draft-icon" aria-label={`Ficha do pedido (${homeDraft.items.length})`} onClick={() => { setOpenResourcesVehicleId(undefined); setOpenDraftVehicleId(primary.id); setTvpfProgressVehicleId(primary.id); }}><PrecisionIcon name="clipboard" /><i>{homeDraft.items.length}</i></button>}
                    <button className="home-message-center" onClick={() => void openMessageCenter()} aria-label={chatUiCopy[settings.language].title}><PrecisionIcon name="chat" />{messageThreads.some(row => unreadMessages(row.overview.messages).length > 0) && <i />}</button>
                    <button onClick={() => setSelected("menu")} aria-label="Menu"><TeslaIcon name="menu" /></button>
                  </div>
                </div>
                <button
                  className="tesla-stage"
                  aria-label={`Abrir ${primary.name}`}
                  onClick={() => openVehicle(primary)}
                >
                  <img
                    src={vehicleImageFor(primary)}
                    onError={(event) => recoverVehicleImage(event, primary)}
                    alt={primary.photo ? primary.name : `Imagem genérica de ${primary.name}`}
                    className={primary.photo ? undefined : "default-vehicle-image"}
                  />
                </button>
                {vehicles.length>1&&<div className="vehicle-navigation-spacer" aria-hidden="true"/>}
                <div
                  className={`shortcut-row ${editingShortcuts ? "editing" : ""}`}
                  hidden
                >
                  {shortcuts.map((id, index) => (
                    <button
                      key={`${id}-${index}`}
                      onPointerDown={startShortcutHold}
                      onPointerUp={stopShortcutHold}
                      onPointerCancel={stopShortcutHold}
                      onContextMenu={(event) => event.preventDefault()}
                      onClick={() =>
                        suppressShortcutClick.current
                          ? (suppressShortcutClick.current = false)
                          : editingShortcuts ? setShortcutSlot(index) : openShortcut(id)
                      }
                    >
                      <i><TeslaIcon name={shortcutInfo[id][0]} /></i>
                      <span>{shortcutInfo[id][1]}</span>
                    </button>
                  ))}
                </div>
                {editingShortcuts && (
                  <div className="shortcut-editor">
                    <p>Prime uma posição e escolhe outra ferramenta. No computador também podes arrastar uma ferramenta para a posição desejada. Os quatro atalhos permanecem sempre ativos.</p>
                    <div className="shortcut-slots">
                      {shortcuts.map((id, index) => (
                        <button key={`${id}-slot`} className={shortcutSlot === index ? "selected" : ""} onClick={() => setShortcutSlot(index)} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); const tool = (draggedShortcut.current || event.dataTransfer.getData("text/plain")) as ShortcutId; if (tool in shortcutInfo) replaceShortcut(index, tool); }}>
                          {index + 1}. {shortcutInfo[id][1]}
                        </button>
                      ))}
                    </div>
                    <div className="shortcut-options">
                      {(Object.keys(shortcutInfo) as ShortcutId[]).filter((id) => !shortcuts.includes(id) || shortcuts[shortcutSlot] === id).map((id) => (
                        <button
                          key={id}
                          draggable
                          onDragStart={(event) => { draggedShortcut.current = id; event.dataTransfer.setData("text/plain", id); }}
                          onDragEnd={() => { draggedShortcut.current = undefined; }}
                          onClick={() => replaceShortcut(shortcutSlot, id)}
                        >
                          {shortcuts[shortcutSlot] === id ? "✓ " : "↔ "}
                          {shortcutInfo[id][1]}
                        </button>
                      ))}
                    </div>
                    <button
                      className="primary"
                      onClick={() => setEditingShortcuts(false)}
                    >
                      Concluir
                    </button>
                  </div>
                )}
                {(primary.tvpfToken || primary.workshopActive) && (
                  <button
                    className={`tvpf-home-banner${primary.tvpfToken&&!primary.tvpfComplete&&primary.tvpfStage===2?" is-preparing":""}${primary.tvpfToken&&primary.tvpfComplete?" is-delivered":""}${!primary.tvpfToken && primaryNeedsAction ? " needs-action" : ""}${primaryAppointment&&!primary.tvpfComplete ? " has-appointment" : ""}`}
                    type="button"
                    onClick={() => { setOpenResourcesVehicleId(primary.tvpfComplete ? undefined : primary.id); setTvpfProgressVehicleId(primary.id); }}
                  >
                    {!primary.tvpfComplete&&<span className="tvpf-home-icon"><PrecisionIcon name="wrench" /></span>}
                    <span className="tvpf-home-copy">
                      <b>{primary.tvpfToken ? (primary.tvpfComplete ? "Veículo entregue" : primary.tvpfStatus || "Preparação do veículo") : tvpfBannerStatusText(primary, settings.language, preparationCopy.fallback)}</b>
                      <span>{primary.tvpfToken ? (primary.tvpfComplete ? "Avalie a sua experiência" : primary.tvpfMessage || "Acompanhe a preparação do seu Tesla") : tvpfActivityText(primary, settings.language)}</span>
                      {!primary.tvpfToken && primaryNeedsAction && <em className="tvpf-action-label">A aguardar a sua resposta</em>}
                    </span>
                    {primaryAppointment&&!primary.tvpfComplete && <span className="tvpf-home-appointment" aria-label={`Marcação confirmada: ${primaryAppointment.fullDate}`}>
                      <span className="tvpf-home-calendar"><small>{primaryAppointment.month}</small><b>{primaryAppointment.day}</b></span>
                      <span className="tvpf-home-calendar-time"><small>{primaryAppointment.weekday}</small><b>{primaryAppointment.time}</b></span>
                    </span>}
                    <span className="tvpf-home-chevron">›</span>
                    {!primary.tvpfToken && <span className="tvpf-home-track" aria-hidden="true">
                      <i style={{ width: `${Math.max(0, Math.min(100, primary.tvpfToken ? (primary.tvpfProgress || 0) : (primary.workshopProgress || 0)))}%` }} />
                    </span>}
                  </button>
                )}
                <div className="vehicle-menu">
                  <button onClick={() => setMaintenanceOpen(true)}>
                    <i><MenuIcon name="maintenance" /></i>
                    <span>
                      <b>Manutenção</b>
                    </span>
                    <span className="maintenance-menu-end">{maintenanceDue.length > 0 && <span className="maintenance-status-dot" role="img" aria-label="Manutenção pendente"/>}›</span>
                  </button>
                  <button onClick={() => { setAccountVehiclePanelId(primary.id); setOpenResourcesVehicleId(undefined); setTvpfProgressVehicleId(primary.id); }}>
                    <i><PrecisionIcon name="wrench" /></i>
                    <span>
                      <b>Assistência</b>
                    </span>
                    <span>›</span>
                  </button>
                  <button onClick={() => setInventoryOpen(true)}>
                    <i><MenuIcon name="vehicles" /></i>
                    <span>
                      <b>Descobrir veículos</b>
                    </span>
                    <span>›</span>
                  </button>
                  <a className="vehicle-menu-link" href="https://www.precisionautotechnik.com/category/ev-parts" target="_blank" rel="noreferrer">
                    <i><PrecisionIcon name="shop" /></i>
                    <span><b>Shop</b></span>
                    <span>↗</span>
                  </a>
                  <button onClick={() => { if (primary.workshopActive || primary.dossierSnapshot || primary.tvpfToken) { setAccountVehiclePanelId(primary.id);setOpenResourcesVehicleId(primary.id);setTvpfProgressVehicleId(primary.id); } else openVehicle(primary); }}>
                    <i><PrecisionIcon name="history" /></i>
                    <span>
                      <b>Histórico e documentos</b>
                    </span>
                    <span>›</span>
                  </button>
                  <button onClick={() => setOnTripVehicleId(primary.id)}><i><PrecisionIcon name="warning" /></i><span><b>Em viagem</b></span><span>›</span></button>
                  <button onClick={() => setRoute("tools")}>
                    <i><PrecisionIcon name="grid" /></i>
                    <span>
                      <b>Todas as ferramentas</b>
                    </span>
                    <span>›</span>
                  </button>
                </div>
                <div className="vehicle-technical-footer">
                  <strong>{primary.name}</strong>
                  {primary.variant && <b>{primary.variant}</b>}
                  <span className="vehicle-identity">{primary.nickname || primary.registration || "Meu carro"}</span>
                  <span className="vehicle-identity">{primary.mileage ? `${primary.mileage.toLocaleString("pt-PT")} km` : "Quilometragem não registada"}</span>
                  <span className="vehicle-identity">VIN: {primary.vin || "Não registado"}</span>
                </div>
              </>;}}</HomeVehiclePager>
            ) : (
              <GuestLanding language={settings.language} apiBase={CONNECTED_API} authenticated contact={{name:settings.profileName,email:settings.profileEmail,phone:settings.profilePhone}} onAddVehicle={()=>setShowVehicle(true)} onMenu={()=>setSelected("menu")} onSignIn={()=>setSelected("profile")} onCreateAccount={()=>{}} />
            )}
          </section>
        )}

        {route === "garage" && (
          <section className={`screen${primary ? "" : " guest-account-screen"}`}>
            <div className="page-title">
              <span>GARAGE</span>
              <h1>{t(settings.language, "yourVehicles")}</h1>
              <p>{t(settings.language, "garageIntro")}</p>
            </div>
            <article className="tesla-connect disabled">
              <div className="tesla-mark">T</div>
              <div>
                <span>CONNECTED VEHICLE</span>
                <h3>Tesla Connected · {t(settings.language, "comingSoon")}</h3>
                <p>{t(settings.language, "manualFirst")}</p>
              </div>
              <span className="soon-pill">V2</span>
            </article>
            <div className="garage-grid">
              {vehicles.map((v) => (
                <article
                  className="garage-card"
                  key={v.id}
                  onClick={() => openVehicle(v)}
                >
                  <img
                    src={vehicleImageFor(v)}
                    onError={(event) => recoverVehicleImage(event, v)}
                    alt={v.photo ? "" : `Imagem genérica de ${v.name}`}
                    className={v.photo ? undefined : "default-vehicle-image"}
                  />
                  <div>
                    <h3>{v.name}</h3>
                    <p>{[v.variant, v.year].filter(Boolean).join(" · ")}</p>
                    <b>
                      {v.soh
                        ? `Battery Health ${v.soh}%`
                        : v.tvpfProgress != null
                          ? `Preparação ${Math.round(v.tvpfProgress)}%`
                          : "Sem avaliação"}
                    </b>
                  </div>
                  <span>›</span>
                </article>
              ))}
            </div>
            <button className="primary" onClick={() => setShowVehicle(true)}>
              {t(settings.language, "addVehicle")} <span>＋</span>
            </button>
            <p className="privacy-note">
              Os veículos manuais permanecem neste dispositivo. Os dados Tesla
              são lidos pelo servidor protegido e os tokens nunca ficam
              guardados no telefone.
            </p>
          </section>
        )}

        {route === "quick" && (
          <section className="screen">
            <div className="page-title">
              <span>QUICK ADD</span>
              <h1>{t(settings.language, "registerWhat")}</h1>
              <p>
                {primary ? `${primary.name}` : t(settings.language, "addFirst")}
              </p>
            </div>
            <div className="quick-list">
              {quickLabels.map((label, i) => (
                <button key={label} onClick={() => addEvent(quickTypes[i])}>
                  <i><PrecisionIcon name={quickIconNames[i]} /></i>
                  <b>{label}</b>
                  <span>＋</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {route === "tools" &&
          (activeTool ? (
            <IntegratedTools
              key={activeTool}
              tool={activeTool}
              vehicle={primary}
              settings={settings}
              apiBase={CONNECTED_API}
              onBack={() => setActiveTool(undefined)}
            />
          ) : (
            <ToolsCatalog language={settings.language} onOpen={openTool} onBack={()=>setRoute("home")} vehicle={primary?{name:primary.name,image:vehicleImageFor(primary)}:undefined}/>
          ))}

        {route === "precision" && (
          <section className="screen">
            <div className="page-title">
              <span>PRECISION</span>
              <h1>Quando precisares de nós.</h1>
              <p>
                Diagnóstico, preparação, manutenção e veículos certificados.
              </p>
            </div>
            <div className="precision-card">
              <span className="precision-theme-logo"><img className="logo-light" src="./precision-logo-primary.png?v=1" alt="Precision Autotechnik" /><img className="logo-dark" src="./precision-logo.png?v=1" alt="" aria-hidden="true" /></span>
              <h2>Precision Autotechnik AG</h2>
              <p>Especialistas em veículos elétricos e Tesla na Suíça.</p>
              <a
                className="primary"
                href="https://wa.me/41782228575"
                target="_blank"
                rel="noreferrer"
              >
                Contactar por WhatsApp <span>→</span>
              </a>
              <a
                className="secondary"
                href="mailto:info@precisionautotechnik.com"
              >
                Enviar email
              </a>
            </div>
            <div className="precision-links">
              <a
                href="https://www.precisionautotechnik.com/cars"
                target="_blank"
                rel="noreferrer"
              >
                <b>Veículos disponíveis</b>
                <span>→</span>
              </a>
              <button onClick={() => primary && shareVehicle(primary)}>
                <b>Partilhar o meu veículo</b>
                <span>↗</span>
              </button>
            </div>
          </section>
        )}
        {primary && (
          <footer className="precision-app-footer">
            <span className="precision-theme-logo"><img className="logo-light" src="./precision-logo-primary.png?v=1" alt="Precision Autotechnik" /><img className="logo-dark" src="./precision-logo.png?v=1" alt="" aria-hidden="true" /></span>
            <span>Precision EV</span>
          </footer>
        )}
      </main>

      {showVehiclePicker && (
        <div
          className="modal-backdrop product-picker-backdrop"
          onClick={() => setShowVehiclePicker(false)}
        >
          <div className="sheet product-picker-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <button className="product-picker-title" onClick={() => setShowVehiclePicker(false)}>
                <b>Produtos</b><span>⌃</span>
              </button>
            </div>
            <div className="vehicle-picker-list">
              {vehicles.map((v) => (
                <button className={`vehicle-picker-main${v.id===primary?.id ? " selected" : ""}`} key={v.id} onClick={() => {
                      selectVehicle(v.id);
                      setShowVehiclePicker(false);
                    }}>
                    <span className="vehicle-picker-copy">
                      <b>{v.nickname || v.registration || "Meu carro"}</b>
                      <span>{v.name}</span>
                    </span>
                    <img src={vehicleImageFor(v)} onError={(event) => recoverVehicleImage(event, v)} alt={v.photo ? v.name : `Imagem genérica de ${v.name}`} className={v.photo ? undefined : "default-vehicle-image"} />
                  </button>
              ))}
            </div>
            <button
              className="product-picker-add"
              onClick={() => {
                setShowVehiclePicker(false);
                setShowVehicle(true);
              }}
            >
              <span>＋</span> Adicionar produto
            </button>
          </div>
        </div>
      )}

      {showVehicle && (
        <div className="modal-backdrop" onClick={() => setShowVehicle(false)}>
          <form
            className="sheet"
            onClick={(e) => e.stopPropagation()}
            onSubmit={async (e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const file = f.get("photo") as File;
              const soh = Number(f.get("soh")) || undefined,
                delta = Number(f.get("delta"));
              const v: Vehicle = {
                id: crypto.randomUUID(),
                nickname: String(f.get("nickname") || ""),
                name: String(f.get("name")),
                make: String(f.get("make")||""),
                modelName: String(f.get("modelName")||""),
                catalogueMakeId: String(f.get("catalogueMakeId")||""),
                registrationDetails: registrationDetailsFromForm(f),
                variant: String(f.get("variant") || ""),
                year: Number(f.get("year")) || undefined,
                firstRegistration: String(f.get("firstRegistration") || ""),
                tyreFront: String(f.get("tyreFront") || "").trim(),
                tyreRear: String(f.get("tyreRear") || "").trim(),
                vin: String(f.get("vin") || ""),
                registration: String(f.get("registration") || ""),
                mileage: Number(f.get("mileage")) || undefined,
                soh,
                delta:
                  Number.isFinite(delta) && String(f.get("delta")) !== ""
                    ? delta
                    : undefined,
                photo: file?.size ? await resizeImage(file, f.get("removeWhite") === "yes") : "",
                primary: vehicles.length === 0,
                events: [],
                batteryMeasurements: [],
              };
              setVehicles((x) => [...x, v]);
              if (soh && v.delta != null)
                void contributeBatteryStudy(
                  v,
                  soh,
                  v.delta,
                  v.mileage,
                  settings.language,
                );
              setShowVehicle(false);
            }}
          >
            <div className="sheet-head">
              <div>
                <span>GARAGE</span>
                <h2>{t(settings.language, "addVehicle")}</h2>
              </div>
              <button type="button" onClick={() => setShowVehicle(false)}>
                ×
              </button>
            </div>
            <label>
              Apelido do carro
              <input name="nickname" placeholder="Ex.: Tereus" required />
            </label>
            <VehicleIdentityFields language={settings.language}/>
            <VehicleRegistrationFields language={settings.language}/>
            <div className="two"><label>Pneus dianteiros<input name="tyreFront" maxLength={30} placeholder="235/45 R18" /></label><label>Pneus traseiros<input name="tyreRear" maxLength={30} placeholder="Igual à frente" /></label></div>
            <div className="two">
              <label>
                Ano
                <input name="year" type="number" inputMode="numeric" />
              </label>
              <label>
                Primeiro registo
                <input
                  name="firstRegistration"
                  placeholder="MM/AAAA"
                  inputMode="numeric"
                />
              </label>
            </div>
            <div className="two">
              <label>
                Matrícula
                <input name="registration" />
              </label>
              <label>
                Quilometragem
                <input name="mileage" type="number" inputMode="numeric" />
              </label>
            </div>
            <label>
              VIN
              <input name="vin" autoCapitalize="characters" maxLength={17} />
            </label>
            <div className="two">
              <label>
                SOH inicial (%)
                <input
                  name="soh"
                  type="number"
                  min="1"
                  max="100"
                  step="0.1"
                  inputMode="decimal"
                />
              </label>
              <label>
                Delta (mV)
                <input name="delta" type="number" min="0" inputMode="decimal" />
              </label>
            </div>
            <label>
              Fotografia de capa
              <input
                name="photo"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              />
            </label>
            <label className="check-label"><input name="removeWhite" type="checkbox" value="yes" /> Integrar fotografia com fundo branco</label>
            <button className="primary">
              {t(settings.language, "save")} <span>→</span>
            </button>
          </form>
        </div>
      )}

      {quickType && primary && (
        <div className="modal-backdrop" onClick={() => setQuickType(undefined)}>
          <form
            className="sheet"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              recordQuick(e.currentTarget);
            }}
          >
            <div className="sheet-head">
              <div>
                <span>QUICK ADD</span>
                <h2>{quickLabels[quickTypes.indexOf(quickType)]}</h2>
                <p>{primary.name}</p>
              </div>
              <button type="button" onClick={() => setQuickType(undefined)}>
                ×
              </button>
            </div>
            {quickType === "battery" ? (
              <>
                <div className="two">
                  <label>
                    SOH (%)
                    <input
                      name="soh"
                      type="number"
                      min="1"
                      max="100"
                      step="0.1"
                      inputMode="decimal"
                      required
                    />
                  </label>
                  <label>
                    Delta (mV)
                    <input
                      name="delta"
                      type="number"
                      min="0"
                      inputMode="decimal"
                      required
                    />
                  </label>
                </div>
                <label>
                  Notas
                  <input
                    name="notes"
                    placeholder="Condições da medição, equipamento…"
                  />
                </label>
              </>
            ) : (
              <>
                <label>
                  Descrição
                  <input
                    name="title"
                    placeholder={quickLabels[quickTypes.indexOf(quickType)]}
                    required={quickType !== "mileage"}
                  />
                  {quickType === "expense" && (
                    <span className="field-help">
                      Indica abaixo o valor na moeda selecionada.
                    </span>
                  )}
                </label>
                {quickType === "expense" && (
                  <label>
                    Valor
                    <input
                      name="amount"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                    />
                  </label>
                )}
              </>
            )}
            <div className="two">
              <label>
                Data
                <input
                  name="date"
                  type="date"
                  defaultValue={new Date().toISOString().slice(0, 10)}
                />
              </label>
              <label>
                Quilometragem
                <input
                  name="odometer"
                  type="number"
                  inputMode="numeric"
                  defaultValue={primary.mileage}
                />
              </label>
            </div>
            <button className="primary">
              {t(settings.language, "save")} <span>→</span>
            </button>
          </form>
        </div>
      )}

      {feedbackOpen && <AppFeedback settings={settings} apiBase={CONNECTED_API} onClose={()=>setFeedbackOpen(false)}/>}
      {menuMotion.value && (
        <div className={"modal-backdrop app-menu-backdrop"+(menuMotion.closing?' is-closing':'')} onClick={() => setSelected(undefined)}>
          <div className="sheet app-menu-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="app-menu-header">
              <button onClick={() => setSelected(undefined)} aria-label="Voltar"><TeslaIcon name="chevronLeft" /></button>
              <h2>Menu</h2>
              <span />
            </div>
            <button className="profile-menu-row" onClick={() => setSelected("profile")}>
              <span className="profile-avatar">{settings.profilePhoto ? <img src={settings.profilePhoto} alt="" /> : (settings.profileName || "P").slice(0, 1).toUpperCase()}</span>
              <span><b>{settings.profileName || "Criar perfil"}</b><small>{settings.profileEmail || "Adiciona os teus dados"}</small></span>
              <i>›</i>
            </button>
            <div className="app-menu-links">
              <button onClick={() => { setSelected(undefined); setRoute("tools"); }}><TeslaIcon name="tools" /><b>Ferramentas</b><span>›</span></button>
              <button onClick={() => { setProductViewId(undefined); setSelected("products"); }}><TeslaIcon name="products" /><b>Os meus produtos</b><span>›</span></button>
              <a href="https://www.precisionautotechnik.com/category/ev-parts" target="_blank" rel="noreferrer"><TeslaIcon name="products" /><b>Shop</b><span>↗</span></a>
              <button onClick={() => setSelected("settings")}><TeslaIcon name="settings" /><b>Configurações</b><span>›</span></button>
              <button data-native-i18n onClick={() => {setSelected(undefined);setFeedbackOpen(true);}}><PrecisionIcon name="chat" /><b>{feedbackTitle(settings.language)}</b><span>›</span></button>
            </div>
            <div className="app-version">
              <b>Precision EV App</b>
              <span>Versão {APP_VERSION}</span>
              <span>Última atualização · {APP_UPDATED}</span>
            </div>
          </div>
        </div>
      )}

      {messageCenterOpen && (
        <div className="message-center-backdrop">
          <section className="message-center-sheet" role="dialog" aria-modal="true" aria-label={chatUiCopy[settings.language].title}>
            <header><button type="button" onClick={() => setMessageCenterOpen(false)} aria-label="Voltar"><TeslaIcon name="chevronLeft" /></button><h2>{chatUiCopy[settings.language].title}</h2><span /></header>
            <div className="message-center-list">
              {messageCenterLoading && <p className="message-center-empty">{chatUiCopy[settings.language].loading}</p>}
              {!messageCenterLoading && !messageThreads.length && <p className="message-center-empty">{chatUiCopy[settings.language].empty}</p>}
              {!messageCenterLoading && messageThreads.map(({ vehicle, overview }) => {
                const dossier = overview.dossier as Record<string, unknown> | null | undefined;
                const request = String(dossier?.request || overview.thread?.subject || "Pedido de assistência");
                const unread = unreadMessages(overview.messages);
                const lastMessage = overview.messages[overview.messages.length - 1];
                const dossierNumber = overview.thread?.dossierNumber || dossier?.dossierNo || dossier?.dossierNumber;
                const updatedAt = lastMessage?.createdAt || overview.thread?.updatedAt || overview.thread?.createdAt || "";
                return <button type="button" className={unread.length ? "has-unread" : undefined} key={`${vehicle.id}-${overview.thread?.id || "thread"}`} onClick={() => { setMessageCenterOpen(false); setOpenResourcesVehicleId(vehicle.id); setOpenChatVehicleId(vehicle.id); setTvpfProgressVehicleId(vehicle.id); }}><span className="message-center-icon"><PrecisionIcon name="wrench" />{unread.length > 0 && <i className="chat-unread-dot" />}</span><span className="message-center-copy"><span className="message-center-heading"><b>{request}</b><time>{messageCenterDate(updatedAt, settings.language)}</time></span><small>{vehicle.nickname || vehicle.name}{dossierNumber ? ` · Auftrag ${dossierNumber}` : ""}</small>{unread.length > 0 && <span className="inbox-unread-label">{chatReadCopy[settings.language]} · {unread.length}</span>}<em>{unread[0]?.body || lastMessage?.body || chatUiCopy[settings.language].open}</em></span><i>›</i></button>;
              })}
            </div>
          </section>
        </div>
      )}

      {onTripVehicleId && vehicles.find(v=>v.id===onTripVehicleId) && <OnTrip vehicle={vehicles.find(v=>v.id===onTripVehicleId)!} apiBase={CONNECTED_API} language={settings.language} onClose={()=>setOnTripVehicleId(undefined)} onReady={()=>{setOnTripVehicleId(undefined);setOpenResourcesVehicleId(undefined);setOpenChatVehicleId(undefined);setOpenDraftVehicleId(onTripVehicleId);setTvpfProgressVehicleId(onTripVehicleId);}}/>}
      {tvpfProgressVehicle?.tvpfToken && !DEMO_MODE && accountVehiclePanelId !== tvpfProgressVehicle.id ? <PreparationExperience profile={settings} language={settings.language} token={tvpfProgressVehicle.tvpfToken} account apiBase={CONNECTED_API} initialPage={openResourcesVehicleId === tvpfProgressVehicle.id ? "resources" : "service"} onClose={()=>{setTvpfProgressVehicleId(undefined);setOpenResourcesVehicleId(undefined);setAccountVehiclePanelId(undefined);}}/> : tvpfProgressVehicle && (
        <TvpfProgressPanel
          vehicle={tvpfProgressVehicle}
          defaultVehicleIndex={Math.max(0, vehicles.findIndex((item) => item.id === tvpfProgressVehicle.id))}
          language={settings.language}
          apiBase={CONNECTED_API}
          openDraftInitially={openDraftVehicleId === tvpfProgressVehicle.id}
          openChatInitially={openChatVehicleId === tvpfProgressVehicle.id}
          openResourcesInitially={openResourcesVehicleId === tvpfProgressVehicle.id}
          initialMaintenanceRequest={maintenanceRequestDraft}
          onClose={() => { setOpenDraftVehicleId(undefined); setTvpfProgressVehicleId(undefined); setOpenChatVehicleId(undefined); setOpenResourcesVehicleId(undefined); setAccountVehiclePanelId(undefined); setMaintenanceRequestDraft(undefined); }}
          onVehicleRefresh={reloadVehicles}
        />
      )}

      {inventoryOpen && <VehicleInventory apiBase={CONNECTED_API} language={settings.language} contact={entryMode==="app"?{name:settings.profileName,email:settings.profileEmail,phone:settings.profilePhone}:undefined} onClose={() => setInventoryOpen(false)} />}

      {selected === "products" && (() => {
        const product = vehicles.find((vehicle) => vehicle.id === productViewId);
        return (
          <div className="modal-backdrop products-page-backdrop" onClick={() => { setProductViewId(undefined); setSelected("menu"); }}>
            <div className="sheet products-page" onClick={(event) => event.stopPropagation()}>
              {!product ? <>
                <div className="products-page-header">
                  <button type="button" onClick={() => setSelected("menu")} aria-label="Voltar"><TeslaIcon name="chevronLeft" /></button>
                  <h2>Os Meus Produtos</h2>
                  <span />
                </div>
                <div className="products-account-list">
                  {vehicles.map((vehicle) => (
                    <button type="button" key={vehicle.id} onClick={() => setProductViewId(vehicle.id)}>
                      <span className="products-account-visual">
                        <img src={vehicleImageFor(vehicle)} onError={(event) => recoverVehicleImage(event, vehicle)} alt={vehicle.photo ? "" : `Imagem genérica de ${vehicle.name}`} className={vehicle.photo ? undefined : "default-vehicle-image"} />
                      </span>
                      <span className="products-account-copy">
                        <b>{vehicle.nickname || vehicle.registration || "Meu carro"}</b>
                        <small>{vehicle.vin || [vehicle.name, vehicle.variant].filter(Boolean).join(" · ")}</small>
                        {vehicle.passport?.accessRole === "DRIVER" && <em>Condutor</em>}
                      </span>
                      <i>›</i>
                    </button>
                  ))}
                </div>
                <button className="products-add-button" type="button" onClick={() => { setSelected(undefined); setShowVehicle(true); }}>
                  Adicionar Produto
                </button>
              </> : <>
                <div className="products-page-header product-detail-header">
                  <button type="button" onClick={() => setProductViewId(undefined)} aria-label="Voltar"><TeslaIcon name="chevronLeft" /></button>
                  <div><h2>{product.nickname || product.registration || "Meu carro"}</h2><p>{product.vin ? `VIN ${product.vin}` : product.name}</p></div>
                  <span />
                </div>
                <div className="product-detail-vehicle">
                  <img src={vehicleImageFor(product)} onError={(event) => recoverVehicleImage(event, product)} alt={product.photo ? "" : `Imagem genérica de ${product.name}`} className={product.photo ? undefined : "default-vehicle-image"} />
                </div>
                <button className="go-to-product" type="button" onClick={() => {
                  setProductViewId(undefined);
                  setEditingVehicleId(canEditVehicleDetails(product) ? product.id : undefined);
                  setSelected(product.id);
                }}>
                  <span>↪</span><b>{canEditVehicleDetails(product) ? "Ir para o Produto" : "Consultar veículo"}</b><i>›</i>
                </button>
                <button className="product-documents-link" type="button" onClick={() => alert("Os documentos e as faturas deste veículo ficarão disponíveis assim que a ligação à API da Trost estiver ativa.")}>
                  <PrecisionIcon name="document" /><span><b>Documentos</b><small>Faturas e documentos · A aguardar API Trost</small></span><i>›</i>
                </button>
                <VehiclePassportAccess apiBase={CONNECTED_API} vehicle={product} onReload={reloadVehicles} onRemoved={() => {
                  setVehicles((current) => current.filter((vehicle) => vehicle.id !== product.id));
                  setProductViewId(undefined);
                }} />
              </>}
            </div>
          </div>
        );
      })()}

      {selected === "profile" && (
        <div className="modal-backdrop app-menu-backdrop" onClick={() => setSelected("menu")}>
          <div className="sheet app-menu-sheet profile-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="app-menu-header">
              <button onClick={() => setSelected("menu")} aria-label="Voltar"><TeslaIcon name="chevronLeft" /></button>
              <h2>{settings.profileName || "Perfil"}</h2><span />
            </div>
            <label className="profile-photo-editor" aria-label="Alterar fotografia">
              <span className="profile-photo-control">
                <span className="profile-avatar large">{settings.profilePhoto ? <img src={settings.profilePhoto} alt="" /> : (settings.profileName || "P").slice(0, 1).toUpperCase()}</span>
                <span className="profile-photo-edit-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
                  </svg>
                </span>
              </span>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={async (event) => {
                const file = event.target.files?.[0];
                event.currentTarget.value = "";
                if (file) setProfilePhotoFile(file);
              }} />
            </label>
            <div className="precision-profile-navigation">
              {[
                ["personal-info","addressBook","Informações pessoais"],
                ["profile-notifications","bell","Notificações"],
                ["profile-wallet","wallet","Carteira"],
                ["profile-commercial","profile","Tornar-me parceiro comercial da Precision"],
                ["profile-orders","orders","Histórico de encomendas"],
                ["profile-security","lock","Segurança e privacidade"],
                ["profile-details","profile","Perfil"],
              ].map(([target,icon,label])=><button key={target} onClick={()=>setSelected(target)}><PrecisionIcon name={icon as PrecisionIconName}/><b>{target==="profile-commercial"?translateShop(label,settings.language):label}</b><span>›</span></button>)}
            </div>
            <button className="profile-sign-out" type="button" disabled={profileSignOutBusy} onClick={() => void signOut()}>
              {profileSignOutBusy ? "A terminar sessão…" : "Terminar sessão"}
            </button>
          </div>
        </div>
      )}

      {selected==="profile-commercial"&&<CommercialPartner apiBase={CONNECTED_API} profile={settings} language={settings.language} demo={DEMO_MODE} onBack={()=>setSelected("profile")}/>}
      {profilePhotoFile && <ProfilePhotoCropper
        file={profilePhotoFile}
        onCancel={() => setProfilePhotoFile(undefined)}
        onSave={async (profilePhoto) => {
          const result = await saveAccountProfile(CONNECTED_API, {
            fullName: settings.profileName || "",
            phone: settings.profilePhone || "",
            address: settings.profileAddress || "",
            profilePhoto,
            notificationsEnabled: Boolean(settings.notificationsEnabled),
          });
          applyAccountProfile(result.account);
          setProfilePhotoFile(undefined);
        }}
      />}

      {selected === "personal-info" && (
        <div className="modal-backdrop app-menu-backdrop" onClick={() => setSelected("profile")}>
          <div className="sheet app-menu-sheet profile-sheet" onClick={(e)=>e.stopPropagation()}>
            <div className="app-menu-header"><button type="button" onClick={()=>setSelected("profile")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Informações pessoais</h2><span/></div>
            <p className="profile-page-intro">Faça a gestão das suas informações pessoais, métodos de contacto e morada.</p>
            <div className="profile-read-list">
              <button onClick={()=>setSelected("profile-edit-name")}><span><small>Nome completo</small><b>{settings.profileName||"Por preencher"}</b></span><i>›</i></button>
              <div><span><small>E-mail principal</small><b>{settings.profileEmail}</b>{settings.emailVerified&&<em>✓ Verificado</em>}</span></div>
              <button onClick={()=>setSelected("profile-edit-phone")}><span><small>Número de telefone principal</small><b>{settings.profilePhone||"Por preencher"}</b></span><i>›</i></button>
              <button onClick={()=>setSelected("profile-edit-address")}><span><small>Morada</small><b className="multiline">{settings.profileAddress||"Por preencher"}</b></span><i>›</i></button>
            </div>
          </div>
        </div>
      )}

      {["profile-edit-name","profile-edit-phone","profile-edit-address"].includes(selected||"")&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("personal-info")}>
          <form className="sheet app-menu-sheet profile-sheet profile-edit-screen" onClick={(e)=>e.stopPropagation()} onSubmit={async(e)=>{e.preventDefault();const form=new FormData(e.currentTarget);const fullName=selected==="profile-edit-name"?[String(form.get("firstName")||"").trim(),String(form.get("lastName")||"").trim()].filter(Boolean).join(" "):settings.profileName;const address=selected==="profile-edit-address"?formatProfileAddress({country:String(form.get("country")||"CH"),address1:String(form.get("address1")||""),address2:String(form.get("address2")||""),postalCode:String(form.get("postalCode")||""),city:String(form.get("city")||"")}):settings.profileAddress;const next={...settings,profileName:fullName,profilePhone:selected==="profile-edit-phone"?String(form.get("phone")||""):settings.profilePhone,profileAddress:address};setSettings(next);const result=await saveAccountProfile(CONNECTED_API,{fullName:next.profileName||"",phone:next.profilePhone||"",address:next.profileAddress||"",profilePhoto:next.profilePhoto||"",notificationsEnabled:Boolean(next.notificationsEnabled)});applyAccountProfile(result.account);setSelected("personal-info");}}>
            <div className="app-menu-header"><button type="button" onClick={()=>setSelected("personal-info")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>{selected==="profile-edit-name"?"Editar nome":selected==="profile-edit-phone"?"Editar número de telefone":"Editar morada"}</h2><span/></div>
            {selected==="profile-edit-name"&&<div className="profile-edit-fields"><label><span>Nome</span><input name="firstName" required defaultValue={(settings.profileName||"").trim().split(/\s+/)[0]||""}/></label><label><span>Apelido</span><input name="lastName" required defaultValue={(settings.profileName||"").trim().split(/\s+/).slice(1).join(" ")}/></label></div>}
            {selected==="profile-edit-phone"&&<div className="profile-edit-fields"><label><span>Telefone de contacto</span><input name="phone" type="tel" required defaultValue={settings.profilePhone||""} placeholder="CH +41 78 000 00 00"/></label></div>}
            {selected==="profile-edit-address"&&<div className="profile-edit-fields"><label><span>País</span><select name="country" defaultValue={parseProfileAddress(settings.profileAddress||'').country}><option value="CH">CH</option><option value="DE">DE</option><option value="FR">FR</option><option value="IT">IT</option><option value="PT">PT</option></select></label><label><span>Morada Linha 1</span><input name="address1" required defaultValue={parseProfileAddress(settings.profileAddress||'').address1}/></label><label><span>Linha de morada 2 (opcional)</span><input name="address2" defaultValue={parseProfileAddress(settings.profileAddress||'').address2}/></label><label><span>Cidade</span><input name="city" required defaultValue={parseProfileAddress(settings.profileAddress||'').city}/></label><label className="postal-field"><span>Código postal</span><input name="postalCode" required inputMode="numeric" defaultValue={parseProfileAddress(settings.profileAddress||'').postalCode}/></label></div>}
            <button className="primary">Atualizar</button>
          </form>
        </div>
      )}

      {selected === "profile-notifications" && (
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile")}><div className="sheet app-menu-sheet profile-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Notificações</h2><span/></div>
          <p className="profile-page-intro">Controle os avisos sobre pedidos, marcações, orçamentos e atualizações da oficina.</p>
          <label className="profile-toggle"><span><b>Notificações da aplicação</b><small>Receber alertas neste dispositivo</small></span><input type="checkbox" disabled={notificationPermission==='unsupported'} checked={Boolean(settings.notificationsEnabled)&&notificationPermission==='granted'} onChange={async(e)=>{let enabled=e.target.checked;if(enabled&&'Notification' in window)enabled=(await Notification.requestPermission())==='granted';setNotificationPermission('Notification' in window?Notification.permission:'unsupported');const next={...settings,notificationsEnabled:enabled};setSettings(next);await saveAccountProfile(CONNECTED_API,{fullName:next.profileName||"",phone:next.profilePhone||"",address:next.profileAddress||"",profilePhoto:next.profilePhoto||"",notificationsEnabled:enabled});}}/></label>
          {notificationPermission==='denied'&&<div className="privacy-note" role="status"><p>As notificações estão bloqueadas neste navegador. Não é possível desbloqueá-las dentro da aplicação.</p><details><summary>Como permitir notificações</summary><ol><li>Abra as definições deste site no navegador, junto ao endereço.</li><li>Em «Notificações», escolha «Permitir».</li><li>Volte à aplicação e ative a opção acima.</li></ol></details><button type="button" onClick={()=>setNotificationPermission('Notification' in window?Notification.permission:'unsupported')}>Verificar novamente</button></div>}
          {notificationPermission==='unsupported'&&<p className="privacy-note">Este navegador não disponibiliza notificações da aplicação. Continue a consultar os avisos dentro da conta.</p>}
        </div></div>
      )}

      {returnedPayment&&!DEMO_MODE&&<ShopPayment apiBase={CONNECTED_API} orderId={returnedPayment} onBack={()=>closeReturnedPayment()} onPaid={()=>closeReturnedPayment(true)}/>}
      {selected==="profile-wallet"&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile")}><div className="sheet app-menu-sheet profile-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile")} aria-label={translateShop('Voltar',settings.language)}><TeslaIcon name="chevronLeft"/></button><h2>{translateShop('Carteira',settings.language)}</h2><span/></div>
          {DEMO_MODE ? <><DemoWallet /><PrecisionPayCard apiBase={CONNECTED_API} language={settings.language} demo/></> : <AccountWallet apiBase={CONNECTED_API} language={settings.language} onShop={()=>{setSelected(undefined);setShopOpen(true);}} onHistory={()=>setSelected("profile-orders")}/>}
        </div></div>
      )}

      {selected==="profile-orders"&&!DEMO_MODE&&<ShopOrders apiBase={CONNECTED_API} language={settings.language} onBack={()=>setSelected("profile")} onShop={()=>{setSelected(undefined);setShopOpen(true);}}/>}
      {selected==="profile-orders"&&DEMO_MODE&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile")}><div className="sheet app-menu-sheet profile-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Histórico de encomendas</h2><span/></div>
          {DEMO_MODE?<DemoOrders/>:<div className="profile-empty-state profile-order-empty"><PrecisionIcon name="orders"/><h3>Sem histórico de encomendas</h3><p>Visite a nossa loja para encontrar peças e acessórios para o seu veículo.</p><a className="primary" href="https://www.precisionautotechnik.com/category/ev-parts" target="_blank" rel="noreferrer">Ir para a loja</a></div>}
        </div></div>
      )}

      {selected==="profile-security"&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile")}><div className="sheet app-menu-sheet profile-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Segurança e Privacidade</h2><span/></div>
          <p className="profile-page-intro security-intro">Proteja a sua conta e controle onde tem sessão iniciada.</p>
          <div className="security-list">
            <button type="button" className="available" onClick={()=>setSelected("profile-security-settings")}><PrecisionIcon name="shield"/><span className="security-option-copy"><b>Segurança</b><small>Palavra-passe, 2FA, recuperação e alertas</small></span><i>›</i></button>
            <button type="button" className="available" onClick={()=>setSelected("profile-sessions")}><PrecisionIcon name="lock"/><span className="security-option-copy"><b>Gestão de início de sessão</b><small>Dispositivos, browsers e última atividade</small></span><i>›</i></button>
            <button type="button" disabled><PrecisionIcon name="grid"/><span className="security-option-copy"><b>Aplicações de terceiros</b><small>RepDoc, Trost e futuros serviços</small></span><em>Planeado</em></button>
            <button type="button" className="available" onClick={()=>setSelected("profile-data-export")}><PrecisionIcon name="document"/><span className="security-option-copy"><b>Solicitar dados</b><small>Exportação RGPD protegida</small></span><i>›</i></button>
          </div>
          <section className="security-danger-zone"><h3>Zona de risco</h3><p>A conta deixará de permitir acesso. Os dados continuam guardados internamente na Precision.</p><button className="profile-delete-account" type="button" onClick={()=>{setAccountDeleteNotice("");setAccountDeleteOpen(true);}}>Eliminar conta</button></section>
          {accountDeleteOpen&&<div className="account-delete-dialog-backdrop" onClick={(event)=>{event.stopPropagation();if(!accountDeleteBusy)setAccountDeleteOpen(false);}}><form className="account-delete-dialog" onClick={(event)=>event.stopPropagation()} onSubmit={async(event)=>{event.preventDefault();const form=new FormData(event.currentTarget);setAccountDeleteBusy(true);setAccountDeleteNotice("");try{await deleteAccount(CONNECTED_API,String(form.get("currentPassword")||""),String(form.get("confirmation")||""));vehiclesHydrated.current=false;setVehicles([]);clearLocalVehicles();setSettings((current)=>({...current,profileName:undefined,profileEmail:undefined,profilePhone:undefined,profileAddress:undefined,profilePhoto:undefined,notificationsEnabled:false,emailVerified:false}));setAccountDeleteOpen(false);setSelected(undefined);setRoute("home");setEntryMode("welcome");}catch(error){const code=error instanceof Error?error.message:"";setAccountDeleteNotice(code==="INVALID_PASSWORD"?"A palavra-passe atual não está correta.":code==="CONFIRMATION_REQUIRED"?"Escreva ELIMINAR para confirmar.":"Não foi possível eliminar a conta. Tente novamente.");}finally{setAccountDeleteBusy(false);}}}>
            <div className="account-delete-warning-icon">!</div><h3>Eliminar esta conta?</h3><p>O acesso será desativado imediatamente e todas as sessões serão terminadas.</p><div className="account-delete-retention"><b>Os dados não serão apagados do servidor.</b><span>Perfil, veículos, marcações, documentos e histórico permanecem guardados internamente e visíveis no Precision Admin.</span></div>{accountDeleteNotice&&<p className="account-delete-error" role="alert">{accountDeleteNotice}</p>}<label>Palavra-passe atual<input name="currentPassword" type="password" autoComplete="current-password" required autoFocus/></label><label>Escreva ELIMINAR para confirmar<input name="confirmation" autoComplete="off" pattern="ELIMINAR" required/></label><button className="account-delete-confirm" disabled={accountDeleteBusy}>{accountDeleteBusy?"A desativar conta…":"Eliminar conta e terminar sessões"}</button><button type="button" className="account-delete-cancel" disabled={accountDeleteBusy} onClick={()=>setAccountDeleteOpen(false)}>Cancelar</button>
          </form></div>}
        </div></div>
      )}

      {selected==="profile-sessions"&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile-security")}><div className="sheet app-menu-sheet profile-sheet session-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile-security")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Gestão de início de sessão</h2><span/></div>
          <SessionManagement apiBase={CONNECTED_API} onSignedOut={()=>{vehiclesHydrated.current=false;setVehicles([]);clearLocalVehicles();setSelected(undefined);setRoute("home");setEntryMode("welcome");}}/>
        </div></div>
      )}

      {selected==="profile-security-settings"&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile-security")}><div className="sheet app-menu-sheet profile-sheet security-settings-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile-security")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Segurança</h2><span/></div>
          <SecuritySettings apiBase={CONNECTED_API}/>
        </div></div>
      )}

      {selected==="profile-data-export"&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile-security")}><div className="sheet app-menu-sheet profile-sheet security-settings-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile-security")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Solicitar dados</h2><span/></div>
          <DataExport apiBase={CONNECTED_API}/>
        </div></div>
      )}

      {selected==="profile-details"&&(
        <div className="modal-backdrop app-menu-backdrop" onClick={()=>setSelected("profile")}><div className="sheet app-menu-sheet profile-sheet" onClick={(e)=>e.stopPropagation()}>
          <div className="app-menu-header"><button onClick={()=>setSelected("profile")} aria-label="Voltar"><TeslaIcon name="chevronLeft"/></button><h2>Perfil</h2><span/></div>
          <div className="profile-about"><PrecisionIcon name="profile"/><p>O seu Perfil Precision guarda as suas preferências e liga os seus veículos, pedidos, documentos e serviços à sua conta.</p>{profileResetNotice&&<p className="profile-reset-success" role="status">{profileResetNotice}</p>}<button type="button" onClick={()=>{setProfileResetNotice("");setProfileResetOpen(true);}}>Repor Perfil Precision</button><small className="profile-reset-backup-note">É criada automaticamente uma cópia recuperável no Precision Admin.</small></div>
          {profileResetOpen&&<div className="profile-reset-dialog-backdrop" onClick={()=>!profileResetBusy&&setProfileResetOpen(false)}><form className="profile-reset-dialog" onClick={(event)=>event.stopPropagation()} onSubmit={async(event)=>{event.preventDefault();const form=new FormData(event.currentTarget);setProfileResetBusy(true);setProfileResetNotice("");try{const result=await resetPrecisionProfile(CONNECTED_API,String(form.get("currentPassword")||""));applyAccountProfile(result.account);vehiclesHydrated.current=true;setVehicles([]);clearLocalVehicles();setProfileResetOpen(false);setProfileResetNotice("Perfil reposto. A cópia de segurança ficou guardada no Precision Admin.");}catch(error){setProfileResetNotice(error instanceof Error&&error.message==="INVALID_PASSWORD"?"A palavra-passe atual não está correta.":"Não foi possível repor o perfil. Tente novamente.");}finally{setProfileResetBusy(false);}}}>
            <div className="profile-reset-warning-icon">!</div><h3>Repor Perfil Precision?</h3><p>A fotografia, telefone, morada, notificações e veículos desta aplicação serão removidos. A conta, segurança, marcações e documentos serão mantidos.</p><p><b>Antes de continuar será guardada uma cópia integral para restauro no Precision Admin.</b></p><label>Confirme a palavra-passe<input name="currentPassword" type="password" autoComplete="current-password" required autoFocus/></label><button className="profile-reset-confirm" disabled={profileResetBusy}>{profileResetBusy?"A criar cópia…":"Criar cópia e repor perfil"}</button><button type="button" className="profile-reset-cancel" disabled={profileResetBusy} onClick={()=>setProfileResetOpen(false)}>Cancelar</button>
          </form></div>}
        </div></div>
      )}

      {maintenanceOpen && primary && <MaintenanceScreen vehicle={primary} onClose={() => setMaintenanceOpen(false)} onAdd={() => setMaintenanceWizardOpen(true)} onRequest={(mileage, note) => { setMaintenanceOpen(false); setOpenResourcesVehicleId(undefined); setMaintenanceRequestDraft({ mileage, note }); setTvpfProgressVehicleId(primary.id); }} />}

      {maintenanceWizardOpen && primary && canManageVehicleServices(primary) && <MaintenanceWizard vehicle={primary} settings={settings} onClose={() => setMaintenanceWizardOpen(false)} onSave={async (item) => { const updated=vehicles.map(vehicle=>vehicle.id===primary.id?{...vehicle,mileage:item.odometer?Math.max(vehicle.mileage||0,item.odometer):vehicle.mileage,events:[item,...vehicle.events],lastUpdated:new Date().toISOString()}:vehicle); await saveAccountVehicles(CONNECTED_API,updated); skipNextVehicleSave.current=true; saveVehicles(updated); setVehicles(updated); setVehicleSyncState("ready"); }} />}

      {editingEvent && (
        <div className="modal-backdrop support-backdrop" onClick={() => setEditingEvent(undefined)}>
          <form className="sheet support-sheet event-edit-sheet" onClick={(event) => event.stopPropagation()} onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const updated: VehicleEvent = {
              ...editingEvent.event,
              title: String(form.get("title") || editingEvent.event.title),
              date: String(form.get("date") || editingEvent.event.date),
              odometer: Number(form.get("odometer")) || undefined,
              amount: form.get("amount") === "" ? undefined : Number(form.get("amount")),
            };
            setVehicles((current) => current.map((vehicle) => vehicle.id === editingEvent.vehicleId
              ? { ...vehicle, events: vehicle.events.map((item) => item.id === updated.id ? updated : item), lastUpdated: new Date().toISOString() }
              : vehicle));
            setEditingEvent(undefined);
          }}>
            <div className="support-header"><button type="button" onClick={() => setEditingEvent(undefined)} aria-label="Voltar"><TeslaIcon name="chevronLeft" /></button><h2>Editar registo</h2><span /></div>
            <label>Descrição<input name="title" defaultValue={editingEvent.event.title} required /></label>
            <div className="two"><label>Data<input name="date" type="date" defaultValue={editingEvent.event.date} required /></label><label>Quilometragem<input name="odometer" type="number" inputMode="numeric" defaultValue={editingEvent.event.odometer} /></label></div>
            <label>Valor ({settings.currency})<input name="amount" type="number" min="0" step="0.01" inputMode="decimal" defaultValue={editingEvent.event.amount} /></label>
            <button className="primary">Guardar alterações <span>→</span></button>
            {!isVerifiedRecord(editingEvent.event) && <button type="button" className="danger" onClick={() => { const target = editingEvent; removeVehicleEvent(target.vehicleId, target.event.id); setEditingEvent(undefined); }}>Apagar registo</button>}
          </form>
        </div>
      )}

      {supportOpen && (
        <div className="modal-backdrop support-backdrop" onClick={() => setSupportOpen(false)}>
          <div className="sheet support-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="support-header">
              <button onClick={() => supportCategory ? setSupportCategory(undefined) : setSupportOpen(false)} aria-label="Voltar"><TeslaIcon name="chevronLeft" /></button>
              <h2>Assistência</h2>
              <span />
            </div>
            <div className="support-categories">
              {["Manutenção", "Exterior", "Interior", "Colisão e vidro", "Ruído e vibração", "Pneus e jantes", "Inspeção", "Bateria e carregamento"].map((category, index) => (
                <button className={supportCategory === category ? "active" : undefined} key={category} onClick={() => setSupportCategory(category)}>
                  <i><TeslaIcon name={(["maintenance", "products", "session", "support", "range", "buying", "history", "battery"] as TeslaIconName[])[index]} /></i>
                  <b>{category}</b><span>›</span>
                </button>
              ))}
            </div>
            {!supportCategory ? (
                <div className="support-intro">
                  <div><h3>Como podemos ajudar?</h3><p>{primary?.name || "O teu veículo"}</p></div>
                  {primary && <img src={vehicleImageFor(primary)} onError={(event) => recoverVehicleImage(event, primary)} alt={primary.photo ? "" : `Imagem genérica de ${primary.name}`} className={primary.photo ? undefined : "default-vehicle-image"} />}
                </div>
            ) : (
              <form className="support-form" onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                const phone = String(form.get("phone") || "");
                const description = String(form.get("description") || "");
                const issue = String(form.get("issue") || "");
                setSettings({ ...settings, profilePhone: phone });
                const message = [
                  "Pedido de assistência Precision EV",
                  `Categoria: ${supportCategory}`,
                  `Área: ${issue}`,
                  `Cliente: ${settings.profileName || "Não indicado"}`,
                  `Telefone: ${phone || "Não indicado"}`,
                  `Veículo: ${primary?.nickname || primary?.name || "Não indicado"}`,
                  primary?.vin ? `VIN: ${primary.vin}` : "",
                  `Descrição: ${description}`,
                ].filter(Boolean).join("\n");
                window.open(`https://wa.me/41782228575?text=${encodeURIComponent(message)}`, "_blank", "noopener");
              }}>
                <span className="support-category-label">{supportCategory}</span>
                <h3>Descreve o problema</h3>
                <p>Inclui quando começou, sintomas e qualquer informação que possa ajudar o diagnóstico.</p>
                <label>Área específica<select name="issue" required><option value="">Selecionar</option>{(({"Manutenção":["Revisão periódica","Filtros e habitáculo","Travões","Suspensão","Climatização"],"Exterior":["Carroçaria","Iluminação","Vidros e espelhos","Portas e bagageira"],"Interior":["Bancos e acabamentos","Ecrã e comandos","Climatização","Ruídos interiores"],"Colisão e vidro":["Colisão","Para-brisas","Vidro lateral","Avaliação de danos"],"Ruído e vibração":["Dianteira","Traseira","Lado esquerdo","Lado direito","Durante aceleração","Durante travagem"],"Pneus e jantes":["Pneu furado","Desgaste","Alinhamento","Jante danificada"],"Inspeção":["Inspeção periódica","Controlo pré-compra","Diagnóstico"],"Bateria e carregamento":["Não carrega","Carregamento lento","Autonomia","Aviso de bateria"]} as Record<string,string[]>)[supportCategory]||[]).map((item)=><option key={item}>{item}</option>)}</select></label>
                <label>Telefone<input name="phone" type="tel" required defaultValue={settings.profilePhone || ""} placeholder="+41 ..." /></label>
                <label>Descrição<textarea name="description" required rows={7} placeholder="Descreve aqui o que está a acontecer..." /></label>
                <button className="primary">Preparar pedido no WhatsApp <span>→</span></button>
              </form>
            )}
          </div>
        </div>
      )}

      {selected === "settings" && (
        <div className="modal-backdrop app-menu-backdrop" onClick={() => setSelected("menu")}>
          <div className="sheet app-menu-sheet settings-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="app-menu-header">
              <button onClick={() => setSelected("menu")} aria-label="Voltar ao menu"><TeslaIcon name="chevronLeft" /></button>
              <h2>{t(settings.language, "settings")}</h2><span />
            </div>
            <div className="settings-intro"><p>Escolha como pretende ver e utilizar a aplicação neste dispositivo.</p></div>
            <section className="settings-group appearance-settings">
              <div className="settings-group-title"><span>Visualização</span><small>A alteração é aplicada imediatamente.</small></div>
              <div className="theme-options" role="group" aria-label="Modo de visualização">
                <button type="button" className={settings.theme === "light" ? "active" : ""} aria-pressed={settings.theme === "light"} onClick={() => changePreferences({theme:"light"})}><i><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg></i><span><b>Claro</b><small>Fundo luminoso</small></span></button>
                <button type="button" className={settings.theme === "dark" ? "active" : ""} aria-pressed={settings.theme === "dark"} onClick={() => changePreferences({theme:"dark"})}><i><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 15.5A8.5 8.5 0 0 1 8.5 4 8.5 8.5 0 1 0 20 15.5Z"/></svg></i><span><b>Escuro</b><small>Fundo escuro</small></span></button>
              </div>
            </section>
            <section className="settings-group">
              <div className="settings-group-title"><span>Idioma e unidades</span><small>Preferências usadas nos cálculos e resultados.</small></div>
              <div className="settings-fields-grid"><label>Idioma
              <select
                value={settings.language}
                onChange={(e) =>
                  changePreferences({
                    language: e.target.value as Settings["language"],
                  })
                }
              >
                <option value="pt">Português</option>
                <option value="de">Deutsch</option>
                <option value="en">English (US)</option>
                <option value="fr">Français</option>
                <option value="it">Italiano</option>
              </select>
              </label>
              <label>Moeda
              <select
                value={settings.currency}
                onChange={(e) =>
                  changePreferences({
                    currency: e.target.value as Settings["currency"],
                  })
                }
              >
                <option>EUR</option>
                <option>CHF</option>
                <option>USD</option>
                <option>GBP</option>
              </select>
              </label>
              <label>Distância
              <select
                value={settings.distance}
                onChange={(e) =>
                  changePreferences({
                    distance: e.target.value as Settings["distance"],
                  })
                }
              >
                <option value="km">Quilómetros</option>
                <option value="mi">Milhas</option>
              </select>
              </label>
              <label>Consumo elétrico
              <select
                value={settings.consumption}
                onChange={(e) =>
                  changePreferences({
                    consumption: e.target.value as Settings["consumption"],
                  })
                }
              >
                <option value="kwh100km">kWh/100 km</option>
                <option value="whkm">Wh/km</option>
                <option value="mikwh">mi/kWh</option>
              </select>
              </label>
              <label>Combustível
              <select
                value={settings.fuel}
                onChange={(e) =>
                  changePreferences({
                    fuel: e.target.value as Settings["fuel"],
                  })
                }
              >
                <option value="l100km">L/100 km</option>
                <option value="mpg">mpg</option>
              </select>
              </label></div>
            </section>
            <p className="privacy-note">
              {preferenceSync === "saving" ? "A guardar as preferências na sua conta…" : preferenceSync === "offline" ? "Preferências guardadas neste dispositivo. Serão sincronizadas quando a ligação regressar." : "Preferências guardadas na sua conta."}
            </p>
          </div>
        </div>
      )}

      {selected &&
        selected !== "settings" &&
        (() => {
          const v = vehicles.find((x) => x.id === selected);
          if (!v) return null;
          const assessment = assessBattery(v);
          const age = vehicleAge(v);
          const isEditingVehicle = editingVehicleId === v.id && canEditVehicleDetails(v);
          const publicPhotos = Array.from(
            new Map(
              [
                ...(v.tvpfPhotos || []),
                ...(v.tvpfHighlights || []).flatMap(
                  (item) => item.photos || [],
                ),
              ]
                .filter((photo) => photo.url)
                .map((photo) => [photo.url, photo]),
            ).values(),
          );
          const historyGroups = Array.from(v.events.reduce((groups, event) => {
            const day = (event.date || event.createdAt || new Date().toISOString()).slice(0, 10);
            const list = groups.get(day) || [];
            list.push(event);
            groups.set(day, list);
            return groups;
          }, new Map<string, VehicleEvent[]>())).sort(([left], [right]) => right.localeCompare(left));
          return (
            <div
              className="modal-backdrop"
              onClick={() => { setSelected(undefined); setEditingVehicleId(undefined); }}
            >
              <div
                className="sheet vehicle-sheet"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sheet-head">
                  <div>
                    <span>
                      {v.precisionCertified
                        ? "PRECISION CERTIFIED"
                        : "MEU VEÍCULO"}
                    </span>
                    <h2>{v.name}</h2>
                    <p>
                      {[v.variant, v.year, v.registration]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <button onClick={() => { setSelected(undefined); setEditingVehicleId(undefined); }}>×</button>
                </div>
                {!isEditingVehicle && v.registrationDetails && Object.values(v.registrationDetails).some(Boolean) && <VehicleRegistrationFields language={settings.language} value={v.registrationDetails} readOnly/>}
                {isEditingVehicle && <>
                <label className="nickname-field">
                  Apelido apresentado na aplicação
                  <input
                    value={v.nickname || ""}
                    placeholder={v.registration || v.name}
                    onChange={(event) =>
                      setVehicles((items) =>
                        items.map((item) =>
                          item.id === v.id
                            ? { ...item, nickname: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                </label>
                <div className="vehicle-edit-grid">
                  <label>Modelo<input value={v.name} onChange={(event) => setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, name: event.target.value,make:undefined,modelName:undefined,catalogueMakeId:undefined } : item))} /></label>
                  <label>Pneus dianteiros<input maxLength={30} placeholder="235/45 R18" value={v.tyreFront || ""} onChange={event => setVehicles(items => items.map(item => item.id === v.id ? {...item,tyreFront:event.target.value} : item))} /></label><label>Pneus traseiros<input maxLength={30} placeholder="Igual à frente" value={v.tyreRear || ""} onChange={event => setVehicles(items => items.map(item => item.id === v.id ? {...item,tyreRear:event.target.value} : item))} /></label>
                  <label>Versão<input value={v.variant || ""} onChange={(event) => setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, variant: event.target.value } : item))} /></label>
                  <label>Ano<input type="number" inputMode="numeric" value={v.year || ""} onChange={(event) => setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, year: Number(event.target.value) || undefined } : item))} /></label>
                  <label>Quilometragem<input type="number" inputMode="numeric" value={v.mileage || ""} onChange={(event) => setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, mileage: Number(event.target.value) || undefined } : item))} /></label>
                  <label>Matrícula<input value={v.registration || ""} onChange={(event) => setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, registration: event.target.value } : item))} /></label>
                  <label>VIN<input maxLength={17} value={v.vin || ""} onChange={(event) => setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, vin: event.target.value.toUpperCase() } : item))} /></label>
                </div>
                <div className="vehicle-photo-edit">
                  <VehicleRegistrationFields language={settings.language} value={v.registrationDetails} onChange={details=>setVehicles(items=>items.map(item=>item.id===v.id?{...item,registrationDetails:details}:item))}/>
                  <span>Fotografia do veículo</span>
                  <img src={vehicleImageFor(v)} onError={(event) => recoverVehicleImage(event, v)} alt={v.photo ? "Fotografia atual do veículo" : "Imagem genérica usada enquanto não existe fotografia"} className={v.photo ? undefined : "default-vehicle-image"} />
                  <div>
                    <label className="vehicle-photo-action">
                      {v.photo ? "Substituir fotografia" : "Adicionar fotografia"}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={async (event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        const remove = confirm("Remover o fundo branco para integrar o carro na aplicação?");
                        const photo = await resizeImage(file, remove);
                        setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, photo } : item));
                        event.target.value = "";
                      }} />
                    </label>
                    {v.photo && <button type="button" className="vehicle-photo-remove" onClick={() => setVehicles((items) => items.map((item) => item.id === v.id ? { ...item, photo: "" } : item))}>Eliminar fotografia</button>}
                  </div>
                </div>
                <button className="primary vehicle-save-button" onClick={() => { setEditingVehicleId(undefined); setSelected(undefined); }}>
                  Guardar alterações <span>→</span>
                </button>
                </>}
                {!isEditingVehicle && (
                  <img className={`detail-photo${v.photo ? "" : " default-vehicle-image"}`} src={vehicleImageFor(v)} onError={(event) => recoverVehicleImage(event, v)} alt={v.photo ? "" : `Imagem genérica de ${v.name}`} />
                )}
                <div className="detail-metrics">
                  <div>
                    <span>Battery Health</span>
                    <b>{v.soh ? `${v.soh}%` : "—"}</b>
                  </div>
                  <div>
                    <span>Quilometragem</span>
                    <b>{v.mileage?.toLocaleString("pt-PT") || "—"} km</b>
                  </div>
                  <div>
                    <span>TVPF</span>
                    <b>
                      {v.tvpfProgress != null
                        ? `${Math.round(v.tvpfProgress)}%`
                        : "—"}
                    </b>
                  </div>
                  <div>
                    <span>Idade</span>
                    <b>{age != null ? `${age.toFixed(1)} anos` : "—"}</b>
                  </div>
                </div>
                {assessment ? (
                  <article className={`battery-result ${assessment.status}`}>
                    <div
                      className="score-ring"
                      style={
                        {
                          "--score": `${assessment.score * 3.6}deg`,
                        } as React.CSSProperties
                      }
                    >
                      <b>{assessment.score}</b>
                      <small>/100</small>
                    </div>
                    <div>
                      <span>BATTERY HEALTH SCORE</span>
                      <h3>
                        {assessment.status === "excellent"
                          ? "Estado excecional"
                          : assessment.status === "veryGood"
                            ? "Muito bom"
                            : assessment.status === "good"
                              ? "Bom / aceitável"
                              : assessment.status === "attention"
                                ? "Atenção recomendada"
                                : "Inspeção recomendada"}
                      </h3>
                      <p>
                        SOH esperado {assessment.low.toFixed(1)}–
                        {assessment.high.toFixed(1)}%. Desvio{" "}
                        {assessment.deviation >= 0 ? "+" : ""}
                        {assessment.deviation.toFixed(1)} pp · equilíbrio{" "}
                        {assessment.deltaStatus}.
                      </p>
                    </div>
                  </article>
                ) : (
                  <button
                    className="empty-battery"
                    onClick={() => {
                      setSelected(undefined);
                      setQuickType("battery");
                    }}
                  >
                    Ainda não existem dados suficientes{" "}
                    <b>Adicionar medição →</b>
                  </button>
                )}
                <div className="vehicle-actions">
                  <button
                    onClick={() => {
                      setSelected(undefined);
                      setQuickType("battery");
                    }}
                  >
                    ＋ Medição
                  </button>
                  <button onClick={() => shareVehicle(v)}>↗ Partilhar</button>
                </div>
                {v.tvpfToken && (
                  <section className="tvpf-public">
                    <div className="section-title">
                      <div>
                        <span>PREPARAÇÃO PRECISION</span>
                        <h2>Progresso TVPF</h2>
                      </div>
                      <b>{Math.round(v.tvpfProgress || 0)}%</b>
                    </div>
                    <div className="tvpf-main-track">
                      <i
                        style={{
                          width: `${Math.max(0, Math.min(100, v.tvpfProgress || 0))}%`,
                        }}
                      />
                    </div>
                    <p className="tvpf-status">
                      {v.tvpfStatus || "Preparação em curso"}
                    </p>
                    {!!publicPhotos.length && (
                      <>
                        <h3>Estado do veículo</h3>
                        <p className="tvpf-gallery-intro">
                          Fotografias publicadas pela Precision durante a
                          inspeção e preparação. Carrega numa fotografia para a
                          ampliar.
                        </p>
                        <div className="tvpf-photo-grid vehicle-condition">
                          {publicPhotos.map((photo, index) => (
                            <a
                              href={photo.url}
                              target="_blank"
                              rel="noreferrer"
                              key={`${photo.url}-${index}`}
                            >
                              <img
                                src={photo.url}
                                alt={
                                  photo.caption ||
                                  "Estado e preparação do veículo"
                                }
                              />
                              <span>
                                {photo.caption ||
                                  photo.phase ||
                                  "Fotografia da inspeção"}
                              </span>
                            </a>
                          ))}
                        </div>
                      </>
                    )}
                    {!!v.tvpfSections?.length && (
                      <div className="tvpf-section-grid">
                        {v.tvpfSections.map((section) => {
                          const pct = Math.round(
                            (section.completed / Math.max(1, section.total)) *
                              100,
                          );
                          return (
                            <article key={section.section}>
                              <header>
                                <b>{section.section}</b>
                                <span>
                                  {section.completed}/{section.total}
                                </span>
                              </header>
                              <div>
                                <i style={{ width: `${pct}%` }} />
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}
                    <h3>Checklist público</h3>
                    {v.tvpfHighlights?.length ? (
                      <div className="tvpf-checks">
                        {v.tvpfHighlights.map((item, index) => (
                          <details className="tvpf-check tvpf-check-expandable" key={`${item.section}-${item.checkpoint}-${index}`}>
                            <summary><i>✓</i><div><small>{item.section}</small><b>{item.checkpoint}</b>{item.result && <p>{item.result}</p>}</div><span>{item.status}</span><em>⌄</em></summary>
                            <div className="tvpf-check-expanded">
                              {!!item.procedure?.length && <><small>PROCEDIMENTO EFETUADO</small><ol>{item.procedure.map((step, stepIndex) => <li key={stepIndex}>{step}</li>)}</ol></>}
                              {!!item.photos?.length && <div className="tvpf-photo-grid compact">{item.photos.map((photo, photoIndex) => <a href={photo.url} target="_blank" rel="noreferrer" key={`${photo.url}-${photoIndex}`}><img src={photo.url} alt={photo.caption || item.checkpoint} /></a>)}</div>}
                              {!!item.documents?.length && <div className="tvpf-check-document-list">{item.documents.map((document, documentIndex) => document.url ? <a href={document.url} target="_blank" rel="noreferrer" key={documentIndex}>{document.name}</a> : <span key={documentIndex}>{document.name}</span>)}</div>}
                            </div>
                          </details>
                        ))}
                      </div>
                    ) : (
                      <p className="empty-copy">
                        Os pontos públicos aparecem aqui à medida que a
                        preparação avança.
                      </p>
                    )}
                  </section>
                )}
                <section className="history-section">
                  <div className="section-title">
                    <h2>{t(settings.language, "history")}</h2>
                    <span>{v.events.length}</span>
                  </div>
                  {historyGroups.length ? (
                    <div className="history-date-groups">{historyGroups.map(([day, events], groupIndex) => (
                      <details className="history-date-group" key={day} open={groupIndex === 0}>
                        <summary><span><b>{new Intl.DateTimeFormat(settings.language === "pt" ? "pt-PT" : settings.language, { dateStyle: "long" }).format(new Date(`${day}T12:00:00`))}</b><small>{events.length} {events.length === 1 ? "atividade" : "atividades"}</small></span><i>⌄</i></summary>
                        <div className="history-timeline">{events.sort((left, right) => String(right.createdAt || right.date).localeCompare(String(left.createdAt || left.date))).map((event) => (
                          <article className="history-row history-timeline-row" key={event.id}>
                            <i><PrecisionIcon name={event.type === "battery" ? "battery" : event.type === "expense" ? "expense" : event.type === "document" ? "document" : "wrench"} /></i>
                            <div><b>{event.title}</b><span>{event.value || event.notes || (event.odometer ? `${event.odometer.toLocaleString()} km` : isVerifiedRecord(event) ? "Registado pela Precision" : "Registado pelo cliente")}</span>
                              {!!event.photos?.length && <div className="history-evidence">{event.photos.slice(0, 8).map((photo, index) => <a href={photo.url} target="_blank" rel="noreferrer" key={`${photo.url}-${index}`}><img src={photo.url} alt={photo.name || event.title} /></a>)}</div>}
                              {!!event.documents?.length && <div className="history-documents">{event.documents.map((document, index) => document.url ? <a href={document.url} target="_blank" rel="noreferrer" key={`${document.name}-${index}`}><PrecisionIcon name="document" />{document.name}</a> : <span key={`${document.name}-${index}`}>{document.name}</span>)}</div>}
                            </div>
                            {!isVerifiedRecord(event) && canManageVehicleServices(v) && <div className="history-actions compact"><button onClick={() => setEditingEvent({ vehicleId: v.id, event })}>Editar</button><button className="delete" onClick={() => removeVehicleEvent(v.id, event.id)}>Apagar</button></div>}
                          </article>
                        ))}</div>
                      </details>
                    ))}</div>
                  ) : (
                    <p className="empty-copy">
                      O histórico começa quando adicionares a primeira medição,
                      manutenção ou documento.
                    </p>
                  )}
                </section>
                <button
                  className="secondary"
                  onClick={() => {
                    setVehicles((x) =>
                      x.map((y) => ({ ...y, primary: y.id === v.id })),
                    );
                    setSelected(undefined);
                  }}
                >
                  Definir como principal
                </button>
                <button className="danger" onClick={() => { setEditingVehicleId(undefined); setSelected("products"); setProductViewId(v.id); }}>
                  {v.passport?.isOwner === false ? "Consultar acesso" : "Gerir acesso ou propriedade"}
                </button>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
